import chroma from "chroma-js";
// import UTIF from "utif";
import { Point } from "./Point";
import { getAbsolutePos } from "./mousePos";
import { Plots, isValidPlot, getUniqueVals} from "babyplots";
import { v4 as uuidv4 } from "uuid";

/**
 * -------------------------------------------------------------------------------------------------
 * Constants
 * -------------------------------------------------------------------------------------------------
 */

/**
 * Predefined colors for nodes types.
 */
export const nodeColors = {
    plot: "#e95420",
    color: "#4960de",
    dataTable: "#222266",
    dataMatrix: "#662222",
    dataArray: "#226622",
    dataValue: "#f2be00",
    tiff: "#4444aa",
    error: "#cc2222",
    legendData: "#965841",
    axisData: "#a32c00"
}

/**
 * Predefined functions for creating the different nodes.
 */
export const nodeTypes = {
    babyplots: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void, showPreviewCallback: (plotData: {}) => void) { return new BabyplotsNode('babyplots', x, y, canvas, updateCallback, showPreviewCallback) },
    pointCloud: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void, showPreviewCallback: (plotData: {}) => void) { return new PointCloudNode('pointCloud', x, y, canvas, updateCallback, showPreviewCallback) },
    shapeCloud: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void, showPreviewCallback: (plotData: {}) => void) { return new ShapeCloudNode('shapeCloud', x, y, canvas, updateCallback, showPreviewCallback) },
    surface: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void, showPreviewCallback: (plotData: {}) => void) { return new SurfaceNode('surface', x, y, canvas, updateCallback, showPreviewCallback) },
    heatMap: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void, showPreviewCallback: (plotData: {}) => void) { return new HeatMapNode('heatMap', x, y, canvas, updateCallback, showPreviewCallback) },
    colorMap: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new ColorMapNode('colorMap', x, y, canvas, updateCallback) },
    dataTable: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new DataTableNode('dataTable', x, y, canvas, updateCallback) },
    matrix2d: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new Matrix2dNode('matrix2d', x, y, canvas, updateCallback) },
    legendData: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new LegendDataNode('legendData', x, y, canvas, updateCallback) },
    axisData: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new AxisDataNode('axisData', x, y, canvas, updateCallback) },
    value: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new ValueNode('value', x, y, canvas, updateCallback) },
    array: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new ArrayNode('array', x, y, canvas, updateCallback) },
    color: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new ColorNode('color', x, y, canvas, updateCallback) },
    // tiff: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new TiffNode('tiff', x, y, canvas, updateCallback) },
    arrayMath: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new ArrayMathNode('arrayMath', x, y, canvas, updateCallback) },
    math: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new MathNode('math', x, y, canvas, updateCallback) },
    flatten: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new FlattenNode('flatten', x, y, canvas, updateCallback) },
    toMatrix: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new ToMatrixNode('toMatrix', x, y, canvas, updateCallback) },
    toColor: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new ToColorNode('toColor', x, y, canvas, updateCallback) },
    concatenate: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new ConcatNode('concatenate', x, y, canvas, updateCallback) },
    split: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new SplitNode('split', x, y, canvas, updateCallback) },
    subset: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new SubsetNode('subset', x, y, canvas, updateCallback) },
    filter: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new FilterNode('filter', x, y, canvas, updateCallback) },
    group: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new GroupNode('group', x, y, canvas, updateCallback) },
    getColumn: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new GetColumnNode('getColumn', x, y, canvas, updateCallback) },
    getRow: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new GetRowNode('getRow', x, y, canvas, updateCallback) },
    index: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new IndexNode('index', x, y, canvas, updateCallback) },
    find: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new FindNode('find', x, y, canvas, updateCallback) },
    minMax: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new MinMaxNode('minMax', x, y, canvas, updateCallback) },
    selector: function (x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) { return new SelectorNode('selector', x, y, canvas, updateCallback) },
}

/**
 * Predefined functions for creating connectors.
 */
export const connectorTypes = {
    plot: function(parentNode: _Node, name: string, isInput: boolean) { return new PlotConnector(parentNode, name, isInput)},
    color: function(parentNode: _Node, name: string, isInput: boolean) { return new ColorConnector(parentNode, name, isInput)},
    matrix2d: function(parentNode: _Node, name: string, isInput: boolean) { return new Matrix2dConnector(parentNode, name, isInput)},
    array: function(parentNode: _Node, name: string, isInput: boolean) { return new ArrayConnector(parentNode, name, isInput)},
    value: function(parentNode: _Node, name: string, isInput: boolean) { return new ValueConnector(parentNode, name, isInput)},
    legendData: function(parentNode: _Node, name: string, isInput: boolean) { return new LegendDataConnector(parentNode, name, isInput)},
    axisData: function(parentNode: _Node, name: string, isInput: boolean) { return new AxisDataConnector(parentNode, name, isInput)},
}

/**
 * Descriptions for nodes in node selection box.
 */
export const nodeDetails = {
    babyplots: [["Plot", "Creates a babyplots visualization by combining multiple plots."], nodeColors.plot],
    pointCloud: [["Plot", "Creates a 3-dimensional point cloud plot where each point is defined by its x, y and z coordinates and its color."], nodeColors.plot],
    shapeCloud: [["Plot", "Creates a 3-dimensional shape cloud plot where each data point is defined by a 3-dimensional shape with x, y and z coordinates and a color."], nodeColors.plot],
    surface: [["Plot", "Creates a 3-dimensional surface plot based on a grid of height values. Grid points can be colored."], nodeColors.plot],
    heatMap: [["Plot", "Creates a 3-dimensional heat map plot. Each data point is represented as a box of a defined height and color."], nodeColors.plot],
    colorMap: [["Mapping", "Maps an array of strings or numbers to a categorical or continuous color scale."], nodeColors.color],
    dataTable: [["Input", "Tabular data with variables as columns and observations in rows.", "Expected format is a tab separated text file. First row may contain a header. Output connectors will be created for each column."], nodeColors.dataArray],
    matrix2d: [["Input", "2-dimensional matrix data with all fields of the same type.", "Expected format is a tab separated text file with or without column- or rownames. One output connector containing the matrix will be created."], nodeColors.dataMatrix],
    legendData: [["Input", "Controls plot legend display."], nodeColors.legendData],
    axisData: [["Input", "Controls plot axis display."], nodeColors.axisData],
    value: [["Input", "Creates a single value."], nodeColors.dataValue],
    array: [["Input", "Creates an array of a defined length filled with a given value."], nodeColors.dataArray],
    color: [["Input", "Creates a single color."], nodeColors.color],
    // tiff: [["Input", "Tiff image stack input"], nodeColors.dataTable],
    arrayMath: [["Data manipulation", "Performs an elementwise operation on one or two arrays and returns the result as an array."], nodeColors.dataArray],
    math: [["Data manipulation", "Performs an operation on one or two values."], nodeColors.dataValue],
    flatten: [["Data manipulation", "Turns a 2-dimensional matrix into a flat array."], nodeColors.dataArray],
    toMatrix: [["Data manipulation", "Turns an array into a 2-dimensional matrix."], nodeColors.dataMatrix],
    toColor: [["Data manipulation", "Ensures all items in an array node are valid colors and outputs a color array. Note: This node performs no conversion! To map colors to items in an array, use the colorMap node."], nodeColors.color],
    concatenate: [["Data manipulation", "Concatenates two or more arrays into one array."], nodeColors.dataArray],
    split: [["Data manipulation", "Splits an array into two or more arrays."], nodeColors.dataArray],
    subset: [["Data manipulation", "Subsets an array by an array of 1s and 0s of the same length as the input array. The output array contains the values of the input array, where the subset array's value is 1."], nodeColors.dataArray],
    filter: [["Data manipulation", "Creates an array of 0s and 1s of the same length as the input array. The output array has a 1 where the value of the input array matches the given value, and a 0 otherwise."], nodeColors.dataArray],
    group: [["Data manipulation", "Creates arrays of 0s and 1s for all unique values of the input array. Similar to the filter node."], nodeColors.dataArray],
    getColumn: [["Data manipulation", "Gets a column of a 2-dimensional matrix based on an index."], nodeColors.dataArray],
    getRow: [["Data manipulation", "Gets a row of a 2-dimensional matrix based on an index."], nodeColors.dataArray],
    index: [["Data manipulation", "Gets the value of an array at a given index."], nodeColors.dataValue],
    find: [["Data manipulation", "Gets the index of a search value in an array. Gets the first occurrence if multiple."], nodeColors.dataValue],
    minMax: [["Data manipulation", "Gets the minimum or maximum value of an array."], nodeColors.dataValue],
    selector: [["Interface", "Creates a selector option from the unique values in the input array and gives the selection as a value output."], nodeColors.dataValue],
}

/**
 * -------------------------------------------------------------------------------------------------
 * Connector classes
 * -------------------------------------------------------------------------------------------------
 */


/**
 * Saved connector information.
 */
export interface ConnectorDump {
    id: string;
    type: string;
    name: string;
    parentNode: string;
    isInput: boolean;
    enabled: boolean;
    parentPosition: number;
}


/**
 * Connector abstract class
 * 
 * With the exception of the PseudoConnector class,
 * all connector classes that inherit from this are
 * used only to check if the connector is of the
 * correct type for a connection.
 * 
 */
export abstract class _Connector {
    id: string;
    type: string;
    name: string;
    placeholder: string;
    color: string;
    xPos: number;
    yPos: number;
    isInput: boolean;
    isConnected: boolean = false;
    enabled: boolean = true;
    parentNode: _Node;
    connectedTo: _Connector[] = [];
    changed: boolean = false;
    autoCreateNodeType: string;
    autoCreateNodeConnector: number;
    destroyed: boolean = false;
    preview: string[] = ["no preview available"]; // by default show no preview, if not implemented in connector type

    /**
     * Connector abstract class.
     * @param parentNode Node that contains this option.
     * @param id uuid identifying this connector.
     * @param name Name for the connector, displayed in node.
     * @param color Color for connector (related to its type).
     * @param isInput Controls position on nodes and connections.
     * @param type Type of input or output of connector for controlling connections.
     * @param placeholder Can store a string, when no other node is connected.
     */
    constructor(parentNode: _Node, name: string, color: string, isInput: boolean, type: string, placeholder?: string) {
        this.parentNode = parentNode;
        this.id = uuidv4();
        this.name = name;
        this.color = color;
        this.isInput = isInput;
        this.type = type;
        this.placeholder = placeholder;
    }

    /**
     * Move connector to a new position.
     * @param x X coordinate of connector.
     * @param y Y coordinate of connector.
     */
    public updatePosition(x: number, y: number): void {
        this.xPos = x;
        this.yPos = y;
    }

    /**
     * Sets enabled parameter to true.
     */
    public enable(): void {
        this.enabled = true;
    }

    /**
     * Sets enabled parameter to false.
     */
    public disable(): void {
        this.enabled = false;
    }

    /**
     * Marks a connector and its connections as ready to be removed.
     */
    public destroy(): void {
        this.destroyed = true;
    }

    /**
     * Connect this connector to connector of other node
     * @param connector Other connector to create new connection.
     */
    public connectTo(connector: _Connector): void {
        this.isConnected = true;
        if (this.isInput) {
            this.connectedTo = [connector];
        } else {
            this.connectedTo.push(connector);
        }
        connector.isConnected = true;
        if (connector.isInput) {
            connector.connectedTo = [this];
        } else {
            connector.connectedTo.push(this);
        }
    }

    /**
     * Disconnect the connector of another node from this connector
     * @param connector the connector to be disconnected
     */
    public disconnectFrom(connector: _Connector) {
        for (let i = 0; i < this.connectedTo.length; i++) {
            const conn = this.connectedTo[i];
            if (conn === connector) {
                this.connectedTo.splice(i, 1);
                if (this.connectedTo.length === 0) {
                    this.isConnected = false;
                }
                break;
            }
        }
    }

    /**
     * Draws a box that shows a preview of the results of a node.
     */
    public showPreview() {
        let ctx = this.parentNode.ctx;
        ctx.save();
        // draw box
        ctx.shadowBlur = 5;
        ctx.shadowColor = "#11111155";
        ctx.beginPath();
        let boxMinWidth = 0;
        // calculate width and height of preview box
        // find longest width of preview lines
        ctx.font = "10pt Open Sans";
        for (let i = 0; i < this.preview.length; i++) {
            const previewLine = this.preview[i];
            let previewLineWidth = ctx.measureText(previewLine).width;
            if (previewLineWidth > boxMinWidth) {
                boxMinWidth = previewLineWidth;
            }
        }
        // test if header needs more space than body
        ctx.font = "11pt Raleway";
        let titleWidth = ctx.measureText(this.name).width;
        if (titleWidth > boxMinWidth) {
            boxMinWidth = titleWidth;
        }
        let boxWidth = boxMinWidth + 20;
        // box body height is line height * lines in preview
        let boxHeight = 23 * this.preview.length;
        ctx.rect(this.xPos, this.yPos, boxWidth, boxHeight + 46); // add header height;
        ctx.fillStyle = "white";
        ctx.fill();
        ctx.restore();
        // draw header
        ctx.fillStyle = this.color;
        ctx.fillRect(this.xPos, this.yPos, boxWidth, 46);
        if (chroma(this.color).luminance() < 0.5) {
            ctx.fillStyle = "white";
        }
        else {
            ctx.fillStyle = "black";
        }
        // title text
        ctx.textAlign = "left";
        ctx.font = "11pt Raleway";
        ctx.fillText(this.name, this.xPos + 10, this.yPos + 17);
        ctx.font = "10pt Open Sans";
        ctx.fillText(this.type, this.xPos + 10, this.yPos + 17 + 23);
        // arrow pointing to connector
        ctx.beginPath();
        ctx.moveTo(this.xPos + 10, this.yPos);
        ctx.lineTo(this.xPos, this.yPos);
        ctx.lineTo(this.xPos, this.yPos + 10);
        ctx.closePath();
        ctx.fill();
        ctx.lineWidth = 1;
        ctx.strokeStyle = this.color;
        ctx.stroke();
        // preview text
        this._renderPreviewText(ctx);
    }

    protected _renderPreviewText(ctx: CanvasRenderingContext2D) {
        ctx.fillStyle = "black";
        for (let i = 0; i < this.preview.length; i++) {
            const previewLine = this.preview[i];
            let ypos = this.yPos + 17 + 46;
            ypos += 23 * i;
            ctx.fillText(previewLine, this.xPos + 10, ypos);
        }
    }

    /**
     * save connector information.
     */
    public save(): ConnectorDump {
        return {
            id: this.id,
            type: this.type,
            name: this.name,
            parentNode: this.parentNode.id,
            isInput: this.isInput,
            enabled: this.enabled,
            parentPosition: this._getParentNodePosition() 
        }
    }

    private _getParentNodePosition() {
        if (this.isInput) {
            return this.parentNode.inputs.indexOf(this);
        } else {
            return this.parentNode.outputs.indexOf(this);
        }
    }
}

/**
 * Simulates a connector, used for temporary connections dragged by the mouse
 */
export class PseudoConnector extends _Connector {
    /**
     * PseudoConnectors are used for simulating a conntector on the mouse cursor when creating a new connection.
     * @param mousePos Point object storing mouse coordinates.
     * @param isInput Simulating input or output.
     */
    constructor(mousePos: Point, isInput: boolean) {
        super(null, "mouseConnector", null, isInput, "pseudo");
        this.xPos = mousePos.x;
        this.yPos = mousePos.y;
    }
}

export class PlotConnector extends _Connector {
    /**
     * Connector for plot data in- and output.
     * @param parentNode Node that contains this option.
     * @param id uuid identifying this connector.
     * @param name Name for the connector, displayed in node.
     * @param isInput Controls position on nodes and connections.
     */
    constructor(parentNode: _Node, name: string, isInput: boolean) {
        super(parentNode, name, nodeColors.plot, isInput, "plot");
    }
}

export class ColorConnector extends _Connector {
    autoCreateNodeType = "color";
    autoCreateNodeConnector = 0;
    previewValues: string[];
    /**
     * Connector for color type in- and output.
     * @param parentNode Node that contains this option.
     * @param id uuid identifying this connector.
     * @param name Name for the connector, displayed in node.
     * @param isInput Controls position on nodes and connections.
     * @param placeholder Can store a color, when no other node is connected.
     */
    constructor(parentNode: _Node, name: string, isInput: boolean, placeholder: string = "#e95420") {
        super(parentNode, name, nodeColors.color, isInput, "color", placeholder);
    }

    showPreview() {
        // format preview text
        let values = this.parentNode.getOutputValue(this);
        if (values === undefined) {
            super.showPreview();
            return;
        }
        if (!Array.isArray(values)) {
            values = [values];
        }
        // display at most 6 values, before showing only head and tail of the color array
        let previewValues = [];
        if (values.length > 6) {
            previewValues = [
                values[0],
                values[1],
                values[2],
                "...",
                values[values.length - 2],
                values[values.length - 1]
            ];
        } else {
            previewValues = values;
        }
        this.previewValues = previewValues;
        // preview has to be an array, to calculate the line height
        // the color connector preview writes one line of text and then one line of color previews
        this.preview = ["["]; // start with open bracket
        for (let i = 0; i < previewValues.length; i++) {
            const v = previewValues[i] as string;
            if (i < previewValues.length - 1) {
                this.preview[0] += v + ", "; // separate values with comma
            } else {
                this.preview[0] += v + "]"; // close bracket at the end.
            }
        }
        // push an empty string to the preview array to calculate the height for the color preview correctly
        this.preview.push("");
        // draw box and preview
        super.showPreview();
    }

    protected _renderPreviewText(ctx: CanvasRenderingContext2D) {
        // draw color strings
        ctx.fillStyle = "black";
        const previewLine = this.preview[0];
        let ypos = this.yPos + 17 + 46;
        ctx.fillText(previewLine, this.xPos + 10, ypos);
        // draw color previews
        if (this.previewValues === undefined) {
            return;
        }
        let bracketWidth = ctx.measureText("[").width;
        let separatorWidth = ctx.measureText(", ").width;
        let xpos = this.xPos + 10 + bracketWidth;
        ypos += 6;
        for (let i = 0; i < this.previewValues.length; i++) {
            const color = this.previewValues[i];
            let colorStringWidth = ctx.measureText(color).width;
            if (color !== "...") {
                ctx.fillStyle = chroma(color).hex();
                ctx.fillRect(xpos, ypos, colorStringWidth, 17);
            }
            xpos += colorStringWidth + separatorWidth;
        }
    }
}

export class Matrix2dConnector extends _Connector {
    /**
     * Connector for matrix2d type in- and output.
     * @param parentNode Node that contains this option.
     * @param id uuid identifying this connector.
     * @param name Name for the connector, displayed in node.
     * @param isInput Controls position on nodes and connections.
     */
    constructor(parentNode: _Node, name: string, isInput: boolean) {
        super(parentNode, name, nodeColors.dataMatrix, isInput, "matrix2d");
    }

    showPreview() {
        // format preview text
        let values = this.parentNode.getOutputValue(this) as string[][];
        if (values === undefined) {
            super.showPreview();
            return;
        }
        // display at most a 4 x 4 matrix before only showing heads and tails padded with "..."
        let previewValues = [];
        if (values.length > 4) {
            if (values[0].length > 4) {
                previewValues = [
                    [
                        values[0][0],
                        values[0][1],
                        "...",
                        values[0][values[0].length - 1]
                    ],
                    [
                        values[1][0],
                        values[1][1],
                        "...",
                        values[1][values[0].length - 1]
                    ],
                    [
                        "...",
                        "...",
                        "...",
                        "...",
                    ],
                    [
                        values[values.length - 1][0],
                        values[values.length - 1][1],
                        "...",
                        values[values.length - 1][values[0].length - 1]
                    ]
                ];
            } else {
                previewValues = [
                    values[0],
                    values[1],
                    [],
                    values[values.length - 1],
                ];
                for (let i = 0; i < values[0].length; i++) {
                    previewValues[2].push("...");
                }
            }
        } else {
            if (values[0].length > 4) {
                previewValues = [];
                for (let i = 0; i < values.length; i++) {
                    const vrow = values[i];
                    previewValues.push([
                        vrow[0],
                        vrow[1],
                        "...",
                        vrow[vrow.length - 1]
                    ]);
                }
            } else {
                previewValues = values;
            }
        }
        // preview is an array of strings, each item in the array represents a line in the preview box
        this.preview = [];
        for (let i = 0; i < previewValues.length; i++) {
            const vrow = previewValues[i] as string[];
            if (i === 0) {
                this.preview.push("[["); // open outer and inner bracket only on first line
            } else {
                this.preview.push(" ["); // open inner bracket
            }
            for (let j = 0; j < vrow.length; j++) {
                const v = vrow[j];
                let vstr: string; // will contain the formatted value
                // test if value is a number
                let vfl = parseFloat(v);
                if (!isNaN(vfl)) {
                    if (vfl % 1 == 0) {
                        // just convert to string if value is an integer
                        vstr = vfl.toString();
                    } else {
                        // otherwise only show 2 decimals
                        vstr = vfl.toFixed(2);
                    }
                } else {
                    vstr = v; // value is not a number
                }
                if (j < vrow.length - 1) {
                    this.preview[i] += vstr + ", "; // separate values with comma
                } else {
                    this.preview[i] += vstr + "]"; // close bracket at the end.
                }
            }
            if (i < previewValues.length - 1) {
                this.preview[i] += ","; // next line
            } else {
                this.preview[i] += "]"; // close outer bracket on last line
            }
        }
        // draw box and preview
        super.showPreview();
    }
}

export class ArrayConnector extends _Connector {
    autoCreateNodeType = "array";
    autoCreateNodeConnector = 0;
    /**
     * Connector for array type in- and output.
     * @param parentNode Node that contains this option.
     * @param id uuid identifying this connector.
     * @param name Name for the connector, displayed in node.
     * @param isInput Controls position on nodes and connections.
     */
    constructor(parentNode: _Node, name: string, isInput: boolean) {
        super(parentNode, name, nodeColors.dataArray, isInput, "array");
    }

    showPreview() {
        // format preview text
        let values = this.parentNode.getOutputValue(this);
        if (values === undefined) {
            super.showPreview();
            return;
        }
        // display at most 6 values, before showing only head and tail of the array
        let previewValues = [];
        if (values.length > 6) {
            previewValues = [
                values[0],
                values[1],
                values[2],
                "...",
                values[values.length - 2],
                values[values.length - 1]
            ];
        } else {
            previewValues = values;
        }
        // preview has to be an array, to calculate the line height
        // the array connector preview only writes only line
        this.preview = ["["]; // start with open bracket
        for (let i = 0; i < previewValues.length; i++) {
            const v = previewValues[i] as string;
            let vstr: string; // will contain the formatted value
            // test if value is a number
            let vfl = parseFloat(v);
            if (!isNaN(vfl)) {
                if (vfl % 1 == 0) {
                    // just convert to string if value is an integer
                    vstr = vfl.toString();
                } else {
                    // otherwise only show 2 decimals
                    vstr = vfl.toFixed(2);
                }
            } else {
                vstr = v; // value is not a number
            }
            if (i < previewValues.length - 1) {
                this.preview[0] += vstr + ", "; // separate values with comma
            } else {
                this.preview[0] += vstr + "]"; // close bracket at the end.
            }
        }
        // draw box and preview
        super.showPreview();
    }
}

export class ValueConnector extends _Connector {
    autoCreateNodeType = "value";
    autoCreateNodeConnector = 0;
    /**
     * Connector for value type in- and output.
     * @param parentNode Node that contains this option.
     * @param id uuid identifying this connector.
     * @param name Name for the connector, displayed in node.
     * @param isInput Controls position on nodes and connections.
     */
    constructor(parentNode: _Node, name: string, isInput: boolean) {
        super(parentNode, name, nodeColors.dataValue, isInput, "value");
    }

    showPreview() {
        // format preview text
        let value = this.parentNode.getOutputValue(this);
        if (value === undefined) {
            super.showPreview();
            return;
        }
        this.preview = [value];
        // draw box and preview
        super.showPreview();
    }
}

export class LegendDataConnector extends _Connector {
    autoCreateNodeType = "legendData";
    autoCreateNodeConnector = 0;
    /**
     * Connector for array type in- and output.
     * @param parentNode Node that contains this option.
     * @param id uuid identifying this connector.
     * @param name Name for the connector, displayed in node.
     * @param isInput Controls position on nodes and connections.
     */
    constructor(parentNode: _Node, name: string, isInput: boolean) {
        super(parentNode, name, nodeColors.legendData, isInput, "legendData");
    }
}

export class AxisDataConnector extends _Connector {
    autoCreateNodeType = "axisData";
    autoCreateNodeConnector = 0;
    /**
     * Connector for array type in- and output.
     * @param parentNode Node that contains this option.
     * @param id uuid identifying this connector.
     * @param name Name for the connector, displayed in node.
     * @param isInput Controls position on nodes and connections.
     */
    constructor(parentNode: _Node, name: string, isInput: boolean) {
        super(parentNode, name, nodeColors.axisData, isInput, "axisData");
    }
}

/**
 * -------------------------------------------------------------------------------------------------
 * Node option classes
 * -------------------------------------------------------------------------------------------------
 */


/**
 * Saved node option information.
 */
export interface NodeOptionDump {
    input: string | number | boolean;
    enabled: boolean;
    connectable: boolean;
    connector: string;
}

export abstract class _NodeOption {
    name: string;
    input: string | boolean | number;
    xPos: number = 0;
    yPos: number = 0;
    div: HTMLDivElement;
    parentNode: _Node;
    height: number = 25;
    enabled: boolean = true;
    connectable: boolean = false;
    connector: _Connector;
    onChange: () => void;

    /**
     * Abstract class for all node options.
     * @param parentNode Node that contains this option.
     * @param name Name for the option, displayed in node.
     * @param input Value of option.
     * @param onChange Callback that will be triggered when option changes.
     */
    constructor(parentNode: _Node, name: string, input: string | boolean | number, onChange?: () => void) {
        this.parentNode = parentNode;
        this.name = name;
        this.input = input;
        this.div = document.createElement("div");
        this.div.className = "npc input";
        let absPos = getAbsolutePos(this.parentNode.canvas, { x: this.xPos, y: this.yPos });
        this.div.style.left = absPos.x + "px";
        this.div.style.top = absPos.y + "px";
        this.div.style.width = this.parentNode.width - 20 + "px";
        let label = document.createElement("label");
        label.innerText = this.name;
        this.div.append(label);
        this.parentNode.canvas.parentElement.append(this.div);
        this.onChange = onChange;
    }

    protected _createConnector() {
        this.connector = new ValueConnector(this.parentNode, this.name, true);
        this.connector.xPos = this.xPos - 3;
        this.connector.yPos = this.yPos + 11;
        this.connectable = true;
    }

    /**
     * Move node option to a new position.
     * @param newPos Point object with coordinates for new position after moving.
     */
    public updatePos(newPos: Point) {
        let absPos = getAbsolutePos(this.parentNode.canvas, newPos);
        this.div.style.left = absPos.x + "px";
        this.div.style.top = absPos.y + "px";
        if (this.connector !== undefined) {
            this.connector.xPos = newPos.x - 5;
            this.connector.yPos = newPos.y + 11;
        }
    }

    /**
     * Sets enabled parameter to true
     */
    public enable() {
        this.enabled = true;
        this.div.style.display = "block";
        this.connector?.enable();
    }

    /**
     * Sets enabled parameter to false
     */
    public disable() {
        this.enabled = false;
        this.div.style.display = "none";
        this.connector?.disable();
    }
    
    public dispose() {
        this.div.querySelectorAll('*').forEach(n => n.remove());
        this.div.remove();
    }

    /**
     * safely change the value of the input.
     */
    public changeValue(value: string | number | boolean) {
        this.input = value;
    }

    /**
     * Make sure the value from connection is valid for this option. If not, return undefined.
     * Implemented in subclasses.
     * @param val Value to check for validity
     */
    protected _checkConnectorInput(val: string | number | boolean) {
        return val;
    }

    /**
     * Disable the html option input element when a connector is used.
     */
    protected _disableOptionElem() {
        // implemented in subclasses.
    }

    /**
     * Enable the html option input element if the connector is not used.
     */
    protected _enableOptionElem() {
        // implemented in subclasses.
    }

    /**
     * Get the value from the input element.
     */
    protected _getOptionInput(): string | number | boolean {
        // implemented in subclasses.
        return
    }

    /**
     * get value either from connection, or if that is unavailable from the option element.
     */
    public getValue(): string | number | boolean {
        if (this.connectable && this.connector !== undefined) {
            let io = this.connector.connectedTo[0];
            if (io !== undefined) {
                let val = io.parentNode.getOutputValue(io);
                let sanitized = this._checkConnectorInput(val)
                if (sanitized !== undefined) {
                    this.input = sanitized;
                    this._disableOptionElem();
                } else if (this.input === undefined) {
                    this.input = this._getOptionInput();
                    this._enableOptionElem();
                }
            } else {
                this._enableOptionElem();
                this.input = this._getOptionInput();
            }
        } else {
            this._enableOptionElem();
            this.input = this._getOptionInput();
        }
        return this.input;
    }

    /**
     * save option input
     */
    public save(): NodeOptionDump {
        return {
            input: this.input,
            enabled: this.enabled,
            connectable: this.connectable,
            connector: this.connector?.id
        }
    }
}
export class DataUploadOption extends _NodeOption {
    inputElem: HTMLInputElement;
    height: number = 60;

    /**
     * Option that contains a file upload field.
     * @param parentNode Node that contains this option.
     * @param name Name for the option, displayed in node.
     */
    constructor(parentNode: _Node, name: string) {
        super(parentNode, name, null);
        this.div.className += " custom-file";
        this.inputElem = document.createElement("input");
        this.inputElem.type = "file";
        this.inputElem.className = "custom-file-input";
        let pNode = this.parentNode as _DataNode;
        this.inputElem.onchange = pNode.handleFiles.bind(pNode);
        this.div.append(this.inputElem);
    }
}
export class GenericSelectorOption extends _NodeOption {
    choices: string[];
    inputElem: HTMLSelectElement;

    /**
     * Option that contains a selector field.
     * @param parentNode Node that contains this option.
     * @param name Name for the option, displayed in node.
     * @param input Value of option.
     * @param choices Choices for selector option dropdown menu.
     * @param onChange Callback that will be triggered when option changes.
     */
    constructor(parentNode: _Node, name: string, input: string, choices: string[], onChange?: () => void) {
        super(parentNode, name, input, onChange);
        this.choices = choices;
        this.inputElem = document.createElement("select");
        this.inputElem.className = "generic-select";
        this.inputElem.name = name;
        this._createChoices(input);
        // handle user input
        this.inputElem.onchange = (function (evt: Event) {
            this.input = this.inputElem.value;
            let pNode = this.parentNode as _Node;
            if (this.onChange !== undefined) {
                this.onChange();
            }
            pNode.updateCallback();
        }).bind(this);
        // append input to option div
        this.div.append(this.inputElem);
    }

    private _createChoices(input: string) {
        for (let i = 0; i < this.choices.length; i++) {
            const choice = this.choices[i];
            let choiceElem = document.createElement("option");
            choiceElem.value = choice;
            choiceElem.innerText = choice;
            if (choice == input) {
                choiceElem.selected = true;
            }
            this.inputElem.append(choiceElem);
        }
    }

    protected _getOptionInput(): string {
        return this.inputElem.value;
    }

    /**
     * Change the value of the input element of a node option.
     * @param value value
     */
    public changeValue(value: string, choices?: string[]): void {
        if (choices !== undefined) {
            this.inputElem.innerHTML = "";
            this.choices = choices;
            this._createChoices(value);
        }
        this.inputElem.value = value;
        super.changeValue(value);
    }
}
export class ColorScaleSelectorOption extends GenericSelectorOption {
    input: string;

    /**
     * Special cas of selector option that contains the colorbrewer2 color choices.
     * @param parentNode Node that contains this option.
     * @param name Name for the option, displayed in node.
     * @param input Value of option.
     * @param onChange Callback that will be triggered when option changes.
     */
    constructor(parentNode: _Node, name: string, input: string, onChange?: () => void) {
        let colorScales = Object.keys(chroma.brewer);
        let colorScalesClean = [];
        for (let i = 0; i < colorScales.length; i++) {
            const cs = colorScales[i];
            const csFirst = cs.charAt(0);
            if (csFirst === csFirst.toUpperCase()) {
                colorScalesClean.push(cs);
            }
        }
        super(parentNode, name, input, colorScalesClean, onChange);
    }
}
export class GenericCheckBoxOption extends _NodeOption {
    input: boolean;
    inputElem: HTMLInputElement;
    /**
     * Option that contains a checkbox element.
     * @param parentNode Node that contains this option.
     * @param name Name for the option, displayed in node.
     * @param input Value of option.
     * @param onChange Callback that will be triggered when option changes.
     */
    constructor(parentNode: _Node, name: string, input: boolean, onChange?: () => void) {
        super(parentNode, name, input, onChange);
        this._createConnector();
        this.inputElem = document.createElement("input");
        this.inputElem.type = "checkbox";
        this.inputElem.name = name;
        this.inputElem.checked = input;
        this.inputElem.className = "generic-checkbox";
        // handle user input
        this.inputElem.onclick = (function (evt: Event) {
            this.input = this.inputElem.checked;
            if (this.onChange !== undefined) {
                this.onChange();
            }
            let pNode = this.parentNode as _Node;
            pNode.updateCallback();
        }).bind(this);
        // append input to option div
        this.div.append(this.inputElem);
    }

    protected _getOptionInput(): boolean {
        return this.inputElem.checked;
    }

    protected _checkConnectorInput(val: string | number | boolean): boolean {
        if (val !== true && val !== false && val !== 0 && val !== 1) {
            this.parentNode.errors.push(`${this.name} not 0 or 1.`);
            return undefined;
        }
        if (val === 0) {
            return false;
        } else if (val === 1) {
            return true;
        } else {
            return val;
        }
    }

    protected _disableOptionElem() {
        this.inputElem.disabled = true;
    }

    protected _enableOptionElem() {
        this.inputElem.disabled = false;
    }

    /**
     * Change the value of the input element of a node option.
     * @param value value
     */
    public changeValue(value: boolean): void {
        this.inputElem.checked = value;
        super.changeValue(value);
    }
}

export class GenericSliderOption extends _NodeOption {
    input: number;
    inputElem: HTMLInputElement;
    /**
     * Option that contains a slider element.
     * @param parentNode Node that contains this option.
     * @param name Name for the option, displayed in node.
     * @param input Value of option.
     * @param onChange Callback that will be triggered when option changes.
     */
    constructor(parentNode: _Node, name: string, input: number, min: number, max: number, onChange?: () => void) {
        super(parentNode, name, input, onChange);
        this._createConnector();
        this.inputElem = document.createElement("input");
        this.inputElem.type = "range";
        this.inputElem.name = name;
        this.inputElem.min = min.toString();
        this.inputElem.max = max.toString();
        this.inputElem.value = input.toString();
        this.inputElem.className = "generic-slider";
        // handle user input
        this.inputElem.onchange = (function (evt: Event) {
            this.input = this.inputElem.value;
            let pNode = this.parentNode as _Node;
            if (this.onChange !== undefined) {
                this.onChange();
            }
            pNode.updateCallback();
        }).bind(this);
        // append input to option div
        this.div.append(this.inputElem);
    }

    protected _getOptionInput(): number {
        return parseFloat(this.inputElem.value);
    }

    protected _checkConnectorInput(val: unknown): number {
        if (val > parseFloat(this.inputElem.max) || val < parseFloat(this.inputElem.min)) {
            this.parentNode.errors.push(`Connected ${this.name} out of range.`);
            return undefined;
        }
        return parseFloat(val as string);
    }

    protected _disableOptionElem() {
        this.inputElem.disabled = true;
    }

    protected _enableOptionElem() {
        this.inputElem.disabled = false;
    }

    /**
     * Change the value of the input element of a node option.
     * @param value value
     */
    public changeValue(value: number): void {
        if (value > parseFloat(this.inputElem.max) || value < parseFloat(this.inputElem.min)) {
            this.parentNode.errors.push(`Connected ${this.name} out of range.`);
        }
        this.inputElem.value = value.toString();
        super.changeValue(value);
    }
}

export class GenericNumberOption extends _NodeOption {
    input: number;
    inputElem: HTMLInputElement;
    /**
     * Option that contains a slider element.
     * @param parentNode Node that contains this option.
     * @param name Name for the option, displayed in node.
     * @param input Value of option.
     * @param step Precision of input number.
     * @param onChange Callback that will be triggered when option changes.
     */
    constructor(parentNode: _Node, name: string, input: number, step: string = "1", onChange?: () => void) {
        super(parentNode, name, input, onChange);
        this._createConnector();
        this.inputElem = document.createElement("input");
        this.inputElem.type = "number";
        this.inputElem.step = step;
        this.inputElem.name = name;
        this.inputElem.value = input.toString();
        this.inputElem.className = "generic-number";
        // handle user input
        this.inputElem.onchange = (function (evt: Event) {
            this.input = parseFloat(this.inputElem.value);
            let pNode = this.parentNode as _Node;
            if (this.onChange !== undefined) {
                this.onChange();
            }
            pNode.updateCallback();
        }).bind(this);
        // append input to option div
        this.div.append(this.inputElem);
    }

    protected _getOptionInput(): number {
        return parseFloat(this.inputElem.value);
    }

    protected _checkConnectorInput(val: unknown): number {
        if (isNaN(val as number) || isNaN(parseFloat(val as string))) {
            this.parentNode.errors.push(`Connected ${this.name} not a number.`);
            return undefined;
        }
        return parseFloat(val as string);
    }

    protected _disableOptionElem() {
        this.inputElem.disabled = true;
    }

    protected _enableOptionElem() {
        this.inputElem.disabled = false;
    }

    /**
     * Change the value of the input element of a node option.
     * @param value value
     */
    public changeValue(value: number): void {
        this.inputElem.value = value.toString();
        super.changeValue(value);
    }
}

export class GenericButtonOption extends _NodeOption {
    buttonElem: HTMLButtonElement;
    /**
     * Option that contains a text input element.
     * @param parentNode Node that contains this option.
     * @param name Name for the option, displayed in node.
     * @param input Value of option.
     * @param onChange Callback that will be triggered when option changes.
     */
    constructor(parentNode: _Node, name: string, icon?: string, onChange?: () => void) {
        super(parentNode, name, null, onChange);
        this.buttonElem = document.createElement("button");
        this.buttonElem.className = "generic-button";
        let iconElem = document.createElement("i");
        iconElem.className = icon;
        this.buttonElem.append(iconElem);
        // handle user input
        this.buttonElem.onclick = (function (evt: Event) {
            let pNode = this.parentNode as _Node;
            if (this.onChange !== undefined) {
                this.onChange();
            }
            pNode.updateCallback();
        }).bind(this);
        // append input to option div
        this.div.append(this.buttonElem);
    }
}

export class GenericTextOption extends _NodeOption {
    input: string;
    inputElem: HTMLInputElement;
    /**
     * Option that contains a text input element.
     * @param parentNode Node that contains this option.
     * @param name Name for the option, displayed in node.
     * @param input Value of option.
     * @param onChange Callback that will be triggered when option changes.
     */
    constructor(parentNode: _Node, name: string, input: string, onChange?: () => void) {
        super(parentNode, name, input, onChange);
        this._createConnector();
        this.inputElem = document.createElement("input");
        this.inputElem.type = "text";
        this.inputElem.name = name;
        this.inputElem.value = input;
        this.inputElem.className = "generic-text";
        // handle user input
        this.inputElem.onchange = (function (evt: Event) {
            this.input = this.inputElem.value;
            let pNode = this.parentNode as _Node;
            if (this.onChange !== undefined) {
                this.onChange();
            }
            pNode.updateCallback();
        }).bind(this);
        // append input to option div
        this.div.append(this.inputElem);
    }

    protected _getOptionInput(): string {
        return this.inputElem.value;
    }

    protected _disableOptionElem() {
        this.inputElem.disabled = true;
    }

    protected _enableOptionElem() {
        this.inputElem.disabled = false;
    }

    /**
     * Change the value of the input element of a node option.
     * @param value value
     */
    public changeValue(value: string): void {
        this.inputElem.value = value;
        super.changeValue(value);
    }
}

/**
 * -------------------------------------------------------------------------------------------------
 * Node classes
 * -------------------------------------------------------------------------------------------------
 */


/**
 * Node saved data.
 */
export interface NodeDump {
    id: string;
    type: string;
    name: string;
    xPos: number;
    yPos: number;
    color: string;
    options: NodeOptionDump[];
    inputs: ConnectorDump[];
    outputs: ConnectorDump[];
    values: any[];
    minimized: boolean;
}

export abstract class _Node {
    private _headerHeight = 23;
    id: string;
    type: string = "";
    name: string;
    xPos: number;
    yPos: number;
    color: string;
    canvas: HTMLCanvasElement;
    ctx: CanvasRenderingContext2D;
    options: _NodeOption[] = [];
    inputs: _Connector[] = [];
    outputs: _Connector[] = [];
    activeOptions: _NodeOption[] = [];
    activeInputs: _Connector[] = [];
    activeOutputs: _Connector[] = [];
    height: number = 45;
    width: number = 200;
    dragged: boolean = false;
    dragOffset: Point;
    extraHeight: number = 0;
    optionOffset: number = 0;
    values: any[] = [];
    inputValues: any[] = [];
    errors: string[] = [];
    minimized: boolean = false;
    updateCallback: () => void;

    /**
     * Abstract class for all node types.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param color Color of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, color: string, canvas: HTMLCanvasElement, updateCallback: () => void) {
        this.id = uuidv4();
        this.name = name;
        this.xPos = x;
        this.yPos = y;
        this.color = color;
        this.canvas = canvas;
        this.ctx = canvas.getContext("2d");
        this.updateCallback = updateCallback;
    }

    /**
     * Returns an array of all input connectors that are enabled.
     */
    protected _getEnabledInputs(): _Connector[] {
        let inputs: _Connector[] = [];
        for (let i = 0; i < this.inputs.length; i++) {
            const input = this.inputs[i];
            if (input.enabled) {
                inputs.push(input);
            }
        }
        return inputs
    }

    /**
     * Returns an array of all output connectors that are enabled.
     */
    protected _getEnabledOutputs(): _Connector[] {
        let outputs: _Connector[] = [];
        for (let i = 0; i < this.outputs.length; i++) {
            const output = this.outputs[i];
            if (output.enabled) {
                outputs.push(output);
            }
        }
        return outputs
    }

    /**
     * Returns an array of all node options that have enabled set as true.
     */
    protected _getEnabledOptions(): _NodeOption[] {
        let options: _NodeOption[] = [];
        for (let i = 0; i < this.options.length; i++) {
            const option = this.options[i];
            if (option.enabled) {
                options.push(option);
            }
        }
        return options
    }

    /**
     * Sets the height of the node by calculating and positioning all input/output connectors and options.
     */
    calcHeight(): void {
        this.activeInputs = this._getEnabledInputs();
        this.activeOutputs = this._getEnabledOutputs();
        this.activeOptions = this._getEnabledOptions();
        this.height = 25 * Math.max(this.activeInputs.length, this.activeOutputs.length) + 25;
        if (!this.minimized) {
            let optionOffset = 0;
            for (let i = 0; i < this.activeOptions.length; i++) {
                const opt = this.activeOptions[i];
                optionOffset += opt.height;
            }
            this.optionOffset = optionOffset;
            this.height += optionOffset;
        } else {
            let optionOffset = 0;
            for (let i = 0; i < this.activeOptions.length; i++) {
                const opt = this.activeOptions[i];
                if (opt.connectable && opt.connector.isConnected) {
                    optionOffset += opt.height;
                }
            }
            this.optionOffset = optionOffset;
            this.height += optionOffset;
        }
        this._positionOptions();
        this._positionConnectors();
    }

    /**
     * Places node options onto correct place in node.
     */
    private _positionOptions(): void {
        let connectedOptions = 0;
        for (let index = 0; index < this.activeOptions.length; index++) {
            const optionElem = this.activeOptions[index];
            if (this.minimized) {
                if (optionElem.connectable && optionElem.connector.isConnected) {
                    optionElem.div.style.display = "block";
                    optionElem.updatePos({
                        x: this.xPos + 5,
                        y: this.yPos + connectedOptions * 25 + 25
                    });
                    connectedOptions += 1;
                } else {
                    optionElem.div.style.display = "none";
                    if (optionElem.connectable) {
                        optionElem.connector.disable();
                    }
                } 
            } else {
                optionElem.div.style.display = "block";
                optionElem.updatePos({
                    x: this.xPos + 5,
                    y: this.yPos + index * 25 + 25
                });
            }
        }
    }

    /**
     * Places input/output connectors onto correct place in node.
     */
    private _positionConnectors(): void {
        let offset = 25;
        offset += this.optionOffset;
        // inputs
        for (let index = 0; index < this.activeInputs.length; index++) {
            const inputElem = this.activeInputs[index];
            inputElem.updatePosition(this.xPos, this.yPos + index * 25 + 12.5 + offset);
        }
        // outputs
        for (let index = 0; index < this.activeOutputs.length; index++) {
            const outputElem = this.activeOutputs[index];
            outputElem.updatePosition(this.xPos + this.width, this.yPos + index * 25 + 12.5 + offset);
        }
    }

    /**
     * Get (first) input connector by name.
     * @param name Name of input to search for.
     */
    public getInputByName(name: string): _Connector {
        for (let i = 0; i < this.inputs.length; i++) {
            const input = this.inputs[i];
            if (input.name == name) {
                return input;
            }
        }
    }

    /**
     * Get (first) output connector by name.
     * @param name Name of output to search for.
     */
    public getOutputByName(name: string): _Connector {
        for (let i = 0; i < this.outputs.length; i++) {
            const output = this.outputs[i];
            if (output.name == name) {
                return output;
            }
        }
    }

    /**
     * Get (first) node option by name.
     * @param name Name of node option to search for.
     */
    public getOptionByName(name: string): _NodeOption {
        for (let i = 0; i < this.options.length; i++) {
            const option = this.options[i];
            if (option.name == name) {
                return option;
            }
        }
    }

    /**
     * Get input value (if calculated) for input connector.
     * @param connector Query input connector.
     */
    public getInputValue(connector: _Connector): any {
        return this.inputValues[this.activeInputs.indexOf(connector)];
    }

    /**
     * Get input value (if calculated) for (first) input connector by name.
     * @param name Name of input to search for.
     */
    public getInputValueByName(name: string): any {
        return this.inputValues[this.activeInputs.indexOf(this.getInputByName(name))];
    }

    /**
     * Get value (if calculated) for output connector.
     * @param connector Query output connector.
     */
    public getOutputValue(connector: _Connector): any {
        return this.values[this.activeOutputs.indexOf(connector)];
    }

    /**
     * Get value (if calculated) for (first) output connector by name.
     * @param name Name of output to search for.
     */
    public getOutputValueByName(name: string): any {
        return this.values[this.activeOutputs.indexOf(this.getOutputByName(name))];
    }

    /**
     * Move node to specified coordinates
     * @param moveTarget Contains x and y coordinates of move
     */
    public move(moveTarget: Point): void {
        // move node
        this.xPos = moveTarget.x + this.dragOffset.x;
        this.yPos = moveTarget.y + this.dragOffset.y;
        // move options
        this._positionOptions();
        // move connectors
        this._positionConnectors();
    }

    /**
     * Returns true if mouse is in header of node.
     * @param mousePos X and y coordinates of mouse on canvas
     */
    public isInHeader(mousePos: Point): boolean {
        return (mousePos.x > this.xPos &&
            mousePos.x < this.xPos + this.width &&
            mousePos.y > this.yPos &&
            mousePos.y < this.yPos + this._headerHeight);
    }

    /**
     * Check whether mouse is in node and if yes, which part of node.
     * @param mousePos X and y coordinates of mouse position in Point object.
     */
    public isInNode(mousePos: Point): {
        over: string;
        conn?: _Connector;
    } {
        // check if the mouse position is within the node at all
        if (mousePos.x > this.xPos - 8 &&
            mousePos.x < this.xPos + this.width + 8 &&
            mousePos.y > this.yPos &&
            mousePos.y < this.yPos + this.height + this.extraHeight) {
            // check if mouse position is in header
            if (mousePos.x > this.xPos &&
                mousePos.x < this.xPos + this.width &&
                mousePos.y > this.yPos &&
                mousePos.y < this.yPos + this._headerHeight) {
                return { over: "header" };
            }
            // check if mouse position is in any of the connectors
            else {
                // option connectors
                for (let i = 0; i < this.options.length; i++) {
                    const opt = this.options[i];
                    if (opt.connectable && opt.connector !== undefined) {
                        const conn = opt.connector
                        if (mousePos.x > conn.xPos - 8 &&
                            mousePos.x < conn.xPos + 8 &&
                            mousePos.y > conn.yPos - 8 &&
                            mousePos.y < conn.yPos + 8) {
                            return { over: "connector", conn: conn };
                        }
                    }
                }
                // input connectors
                for (let i = 0; i < this.activeInputs.length; i++) {
                    const conn = this.activeInputs[i];
                    if (mousePos.x > conn.xPos - 8 &&
                        mousePos.x < conn.xPos + 8 &&
                        mousePos.y > conn.yPos - 8 &&
                        mousePos.y < conn.yPos + 8) {
                        return { over: "connector", conn: conn };
                    }
                }
                // output connectors
                for (let i = 0; i < this.activeOutputs.length; i++) {
                    const conn = this.activeOutputs[i];
                    if (mousePos.x > conn.xPos - 8 &&
                        mousePos.x < conn.xPos + 8 &&
                        mousePos.y > conn.yPos - 8 &&
                        mousePos.y < conn.yPos + 8) {
                        return { over: "connector", conn: conn };
                    }
                }
                // the mouse is somewhere in the body of the node
                return { over: "body" };
            }
        }
        else {
            return null;
        }
    }

    /**
     * Draw node on canvas.
     */
    public draw(): void {
        // draw outline
        this.ctx.lineWidth = 2;
        this.ctx.save();
        if (this.dragged) {
            this.ctx.shadowBlur = 10;
            this.ctx.shadowColor = "#11111199";
        } else {
            this.ctx.shadowBlur = 5;
            this.ctx.shadowColor = "#11111155";
        }
        this.ctx.beginPath();
        this.ctx.rect(this.xPos, this.yPos, this.width, this.height + this.extraHeight);
        this.ctx.fillStyle = "white";
        this.ctx.fill();
        this.ctx.restore();
        this.ctx.fillStyle = "#fff";
        this.ctx.fill();
        // this.ctx.strokeStyle = this.color;
        // this.ctx.stroke();
        // draw header
        this.ctx.fillStyle = this.color;
        this.ctx.fillRect(this.xPos, this.yPos, this.width, this._headerHeight);
        if (chroma(this.color).luminance() < 0.5) {
            this.ctx.fillStyle = "white";
        }
        else {
            this.ctx.fillStyle = "black";
        }
        this.ctx.textAlign = "left";
        this.ctx.font = "12pt Raleway";
        this.ctx.fillText(this.name, this.xPos + 10, this.yPos + 17);
        // draw connectors
        // make space for options
        let offset = 25;
        offset += this.optionOffset;
        // option connectors
        for (let index = 0; index < this.options.length; index++) {
            const opt = this.options[index];
            if (opt.enabled && opt.connectable && opt.connector && opt.connector.enabled) {
                this.ctx.beginPath();
                this.ctx.arc(opt.connector.xPos, opt.connector.yPos, 8, 0, 2 * Math.PI);
                this.ctx.fillStyle = "white";
                this.ctx.fill();
                this.ctx.strokeStyle = opt.connector.color;
                this.ctx.stroke();
                if (opt.connector.isConnected) {
                    this.ctx.beginPath();
                    this.ctx.arc(opt.connector.xPos, opt.connector.yPos, 5, 0, 2 * Math.PI);
                    this.ctx.fillStyle = opt.connector.color;
                    this.ctx.fill();
                }
            }
        }
        // inputs
        for (let index = 0; index < this.activeInputs.length; index++) {
            const inputElem = this.activeInputs[index];
            if (inputElem.enabled) {
                this.ctx.beginPath();
                this.ctx.arc(inputElem.xPos, inputElem.yPos, 8, 0, 2 * Math.PI);
                this.ctx.fillStyle = "white";
                this.ctx.fill();
                this.ctx.strokeStyle = inputElem.color;
                this.ctx.stroke();
                if (inputElem.isConnected) {
                    this.ctx.beginPath();
                    this.ctx.arc(inputElem.xPos, inputElem.yPos, 5, 0, 2 * Math.PI);
                    this.ctx.fillStyle = inputElem.color;
                    this.ctx.fill();
                }
                this.ctx.font = "11px Open Sans";
                this.ctx.fillStyle = "black";
                this.ctx.fillText(inputElem.name, this.xPos + 15, this.yPos + index * 25 + 15 + offset);
            }
        }
        // outputs
        for (let index = 0; index < this.activeOutputs.length; index++) {
            const outputElem = this.activeOutputs[index];
            if (outputElem.enabled) {
                this.ctx.beginPath();
                this.ctx.arc(outputElem.xPos, outputElem.yPos, 8, 0, 2 * Math.PI);
                this.ctx.fillStyle = "white";
                this.ctx.fill();
                this.ctx.strokeStyle = outputElem.color;
                this.ctx.stroke();
                if (outputElem.isConnected) {
                    this.ctx.beginPath();
                    this.ctx.arc(outputElem.xPos, outputElem.yPos, 5, 0, 2 * Math.PI);
                    this.ctx.fillStyle = outputElem.color;
                    this.ctx.fill();
                }
                this.ctx.font = "11px Verdana";
                this.ctx.fillStyle = "black";
                this.ctx.textAlign = "right";
                this.ctx.fillText(outputElem.name, this.xPos + this.width - 15, this.yPos + index * 25 + 15.5 + offset);
            }
        }
        this._showErrors();
    }

    /**
     * Retrieve all inputs from connected nodes and calculate output values.
     * 
     * If called with the connector argument, returns the value of that connector,
     * otherwise returns undefined.
     * 
     * After calculation, all values can be retrieved from values property.
     * 
     * @param connector Connector object to query for value.
     */
    public calculateValues(connector?: _Connector): any {
        // empty errors array.
        this.errors = [];
        // retrieve connected data
        let inputVals = [];
        for (let i = 0; i < this.activeInputs.length; i++) {
            const input = this.activeInputs[i];
            let io = input.connectedTo[0];
            if (io === undefined) {
                inputVals.push(undefined);
                continue;
            }
            let iRes: any;
            if (io.changed) {
                let iRes = io.parentNode.calculateValues(io);
                if (io.parentNode.errors.length !== 0) {
                    inputVals.push(undefined);
                    continue;
                }
            } else {
                iRes = io.parentNode.getOutputValue(io);
            }
                inputVals.push(iRes);
                io.changed = false;
        }
        this.inputValues = inputVals;
        this._performNodeCalculation();
        if (connector !== undefined) {
            let connIndex = this.outputs.indexOf(connector);
            if (connIndex === -1) {
                return undefined;
            }
            return this.values[connIndex];
        }
        this._showErrors();
    }

    /**
     * 
     */
    protected _performNodeCalculation() {
        for (let i = 0; i < this.activeOutputs.length; i++) {
            const output = this.activeOutputs[i];
            if (output.isConnected && output.connectedTo.length !== 0) {
                for (let j = 0; j < output.connectedTo.length; j++) {
                    const connectedConnector = output.connectedTo[j];
                    connectedConnector.parentNode?.fetchUpdates();
                }
                output.changed = false;
            }
        }
    }

    /**
     * fetchUpdates
     */
    public fetchUpdates() {
        let hasUpdates = false;
        // get updates from inputs
        for (let i = 0; i < this.activeInputs.length; i++) {
            const input = this.activeInputs[i];
            let io = input.connectedTo[0];
            if (io === undefined) {
                continue;
            } else if (io.changed) {
                this.inputValues[i] = io.parentNode.getOutputValue(io);
                hasUpdates = true;
            }
        }
        // get updates from option connectors
        for (let i = 0; i < this.options.length; i++) {
            const opt = this.options[i];
            if (opt.enabled && opt.connectable && opt.connector !== undefined) {
                let io = opt.connector.connectedTo[0];
                if (io === undefined) {
                    continue
                } else if (io.changed) {
                    hasUpdates = true;
                }
            }
        }
        if (hasUpdates) {
            this._performNodeCalculation();
        }
    }

    protected _showErrors() {
        for (let i = 0; i < this.errors.length; i++) {
            const error = this.errors[i];
            this.ctx.save();
            this.ctx.fillStyle = nodeColors.error;
            this.ctx.beginPath();
            this.ctx.rect(this.xPos, this.yPos - (i + 1) * 2 * this._headerHeight, this.width, this._headerHeight * 2);
            this.ctx.fill();
            this.ctx.lineWidth = 1;
            this.ctx.strokeStyle = "#ffffff";
            this.ctx.stroke();
            this.ctx.restore();
            if (chroma(nodeColors.error).luminance() < 0.5) {
                this.ctx.fillStyle = "white";
            }
            else {
                this.ctx.fillStyle = "black";
            }
            this.ctx.textAlign = "left";
            this.ctx.font = "11px Open Sans";
            this.ctx.fillText("!! Error !!", this.xPos + 10, this.yPos - (2 * i + 2) * this._headerHeight + 17);
            this.ctx.fillText(error, this.xPos + 10, this.yPos - (2 * i + 1) * this._headerHeight + 17);
        }
    }

    public toggleMinimize() {
        this.minimized = !this.minimized;
        this.calcHeight();
        this.updateCallback();
        for (let idx = 0; idx < this.options.length; idx++) {
            const opt = this.options[idx];
            if (!this.minimized && opt.connectable && !opt.connector.enabled)
            opt.connector.enable();
        }
    }

    public dispose() {
        for (let i = 0; i < this.options.length; i++) {
            this.options[i].dispose();
        }
    }

    /**
     * save node as javascript object.
     */
    public save(): NodeDump {
        return {
            id: this.id,
            type: this.type,
            name: this.name,
            xPos: this.xPos,
            yPos: this.yPos,
            color: this.color,
            options: this.options.map((opt) => opt.save()),
            inputs: this.inputs.map((inp) => inp.save()),
            outputs: this.outputs.map((out) => out.save()),
            values: this.values,
            minimized: this.minimized
        }
    }
}

export abstract class _outputNode extends _Node {
    previewCanvas: HTMLCanvasElement;
    p: Plots;
    plotData = {};
    showPreviewCallback: (plotData: {}) => void;

    /**
     * Abstract class for all node types that create a babyplot.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void, showPreviewCallback: (plotData: {}) => void) {
        super(name, x, y, nodeColors.plot, canvas, updateCallback);
        // height for canvas
        this.extraHeight += 200;
        this.previewCanvas = document.createElement("canvas");
        this.previewCanvas.width = 180;
        this.previewCanvas.height = 180;
        this.previewCanvas.style.width = "180px";
        this.previewCanvas.style.height = "180px";
        this.previewCanvas.style.position = "absolute";
        let abspos = getAbsolutePos(this.canvas, { x: this.xPos, y: this.yPos });
        this.previewCanvas.style.left = abspos.x + 10 + "px";
        this.previewCanvas.style.top = abspos.y + this.height + "px";
        this.previewCanvas.style.backgroundColor = "white";
        this.canvas.parentElement.append(this.previewCanvas);
        this.previewCanvas.id = "previewCanvas-" + this.id;
        this.showPreviewCallback = showPreviewCallback;
    }

    /**
     * Draw node on canvas
     */
    public draw() {
        super.draw();
        this._drawPreview();
    }

    /**
     * Draw the canvas with the babyplot preview.
     */
    protected _drawPreview() {
        let abspos = getAbsolutePos(this.canvas, { x: this.xPos, y: this.yPos });
        this.previewCanvas.style.left = abspos.x + 10 + "px";
        this.previewCanvas.style.top = abspos.y + this.height + 10 + this.extraHeight - 200 + "px";
    }

    /**
     * 
     */
    protected _performNodeCalculation() {
        if (isValidPlot(this.plotData)) {
            if (this.p !== undefined) {
                this.p.dispose();
                this.p = undefined;
            }
            this.p = new Plots(this.previewCanvas.id, { backgroundColor: "#ffffffff" });
            this.p.fromJSON(this.plotData);
            this.p.doRender();
            let showPreviewBtn = this.getOptionByName("export/publish") as GenericButtonOption;
            if (showPreviewBtn !== undefined) {
                showPreviewBtn.onChange = this.showPreviewCallback.bind(null, this.plotData);
                showPreviewBtn.enable();
                this.calcHeight();
                this.updateCallback();
            }
            super._performNodeCalculation();
        }
    }

    public dispose() {
        if (this.p !== undefined) {
            this.p.dispose();
            this.p = undefined;
        }
        this.previewCanvas.remove();
        super.dispose();
    }
}

export class BabyplotsNode extends _outputNode {
    n: number = 3;
    type: string = "babyplots";
    /**
     * Abstract class for all plot type babyplot nodes.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     * @param showPreviewCallback Function that is called when the export/publish button is pressed.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void, showPreviewCallback: (plotData: {}) => void) {
        super(name, x, y, canvas, updateCallback, showPreviewCallback);
        this.inputs.push(new PlotConnector(this, 'plot 1', true));
        this.inputs.push(new PlotConnector(this, 'plot 2', true));
        let showBtn = new GenericButtonOption(this, "export/publish", "fas fa-file-export");
        showBtn.disable();
        this.options.push(
            showBtn,
            new GenericTextOption(this, "shape title", "", (function () { (this as BabyplotsNode)._performNodeCalculation() }).bind(this)),
            new GenericNumberOption(this, "xScale", 1, "0.001", (function () { (this as BabyplotsNode)._performNodeCalculation() }).bind(this)),
            new GenericNumberOption(this, "yScale", 1, "0.001", (function () { (this as BabyplotsNode)._performNodeCalculation() }).bind(this)),
            new GenericNumberOption(this, "zScale", 1, "0.001", (function () { (this as BabyplotsNode)._performNodeCalculation() }).bind(this)),
        );
        this.calcHeight();
    }

    protected _performNodeCalculation() {
        let freeInputs = 0;
        let plotDataArr = [];
        for (let i = 0; i < this.inputs.length; i++) {
            const plotInput = this.inputs[i];
            if (plotInput.isConnected) {
                let pD = this.getInputValue(plotInput);
                if (pD === undefined) {
                    continue;
                }
                if (!isValidPlot(pD)) {
                    continue;
                }
                plotDataArr.push(pD);
            } else {
                freeInputs += 1;
            }
        }
        if (plotDataArr.length === 0) {
            return;
        }

        // create plotData object from input plot data.
        // create from first plot
        this.plotData = plotDataArr[0];
        this.plotData["xScale"] = this.getOptionByName("xScale").getValue();
        this.plotData["yScale"] = this.getOptionByName("yScale").getValue();
        this.plotData["zScale"] = this.getOptionByName("zScale").getValue();
        this.plotData["shapeLegendTitle"] = this.getOptionByName("shape title").getValue();

        if (plotDataArr.length > 1) {
            // add other plots
            for (let i = 1; i < plotDataArr.length; i++) {
                const pD = plotDataArr[i];
                this.plotData["plots"].push(pD["plots"][0]);
            }
        }
        // create new plot inputs if none are free.
        if (freeInputs === 0) {
            this.inputs.push(new PlotConnector(this, "plot " + (this.n).toString(), true));
            this.n += 1;
            this.calcHeight();
        }

        super._performNodeCalculation();
    }

}

// export abstract class imgStackNode extends _outputNode {
//     type: string = "imgStack";

//     /**
//      * TODO
//      * 
//      * Image stack node.
//      * @param id uuid identifying the node.
//      * @param name Name displayed as title of the node.
//      * @param x X coordinate of node. Defined as top left corner of node.
//      * @param y Y corrdinate of node. Defined as top left corner of node.
//      * @param canvas Canvas element of the node creator.
//      * @param updateCallback Function that is called when node has changed and needs redraw.
//      */
//     constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void, showPreviewCallback: (plotData: {}) => void) {
//         super(name, x, y, canvas, updateCallback, showPreviewCallback);

//     }
// }

export abstract class _PlotNode extends _outputNode {
    colorBy: string = '';
    previewColors: string[];
    prevPlotData = {};

    /**
     * Abstract class for all plot type babyplot nodes.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void, showPreviewCallback: (plotData: {}) => void) {
        super(name, x, y, canvas, updateCallback, showPreviewCallback);
        this.inputs.push(new ColorConnector(this, 'color', true));
        this.inputs.push(new ArrayConnector(this, 'colorVar', true));
        this.inputs.push(new LegendDataConnector(this, 'legendData', true));
        this.inputs.push(new AxisDataConnector(this, 'axisData', true));
        this.outputs.push(new PlotConnector(this, 'plotData', false));
        let showBtn = new GenericButtonOption(this, "export/publish", "fas fa-file-export");
        showBtn.disable();
        this.options.push(
            showBtn,
            new GenericNumberOption(this, "xScale", 1, "0.001", (function () { (this as _PlotNode)._performNodeCalculation() }).bind(this)),
            new GenericNumberOption(this, "yScale", 1, "0.001", (function () { (this as _PlotNode)._performNodeCalculation() }).bind(this)),
            new GenericNumberOption(this, "zScale", 1, "0.001", (function () { (this as _PlotNode)._performNodeCalculation() }).bind(this)),
            new GenericSelectorOption(this, "colorBy", "direct", ["categories", "values", "direct"], (function () { (this as _PlotNode)._switchColorBy() }).bind(this)),
            new ColorScaleSelectorOption(this, "colorScale", "Paired", (function () { (this as _PlotNode)._applyColors() }).bind(this)),
            new GenericCheckBoxOption(this, "inverted", false, (function () { (this as _PlotNode)._applyColors() }).bind(this))
        );
        this._switchColorBy();
        this.calcHeight();
    }

    /**
     * Show/hide the respective options and connectors depending on which color-by type was chosen.
     */
    private _switchColorBy(): void {
        let colorByOption = (this.getOptionByName("colorBy") as GenericSelectorOption);
        let prevColorBy = this.colorBy;
        this.colorBy = colorByOption.input as string;
        if (prevColorBy !== this.colorBy) {
            let colorInput = this.getInputByName("color");
            let colorVarInput = this.getInputByName("colorVar");
            let colorScaleOption = this.getOptionByName("colorScale");
            let invertedOption = this.getOptionByName("inverted");
            if (this.colorBy === "direct") {
                colorInput.enable();
                colorVarInput.disable();
                colorScaleOption.disable();
                invertedOption.disable();
                if (["categories", "values"].indexOf(prevColorBy) !== -1)
                    this.extraHeight -= 35;
            } else {
                colorInput.disable();
                colorVarInput.enable();
                colorScaleOption.enable();
                invertedOption.enable();
                if (prevColorBy === "direct") {
                    this.extraHeight += 35;
                }
            }
            this.calcHeight();
        }
        this._applyColors();
        this.updateCallback();
    }

    /**
     * Draw preview canvas as well as color preview if needed.
     */
    protected _drawPreview(): void {
        super._drawPreview();
        if (this.colorBy !== "direct") {
            this._drawColorPreview();
        }
    }

    /**
     * Draws preview of selected color scale.
     */
    protected _drawColorPreview(): void {
        let nSteps = 10;
        this.ctx.fillStyle = "white";
        this.ctx.fillRect(this.xPos + 10, this.yPos + this.height, this.width - 20, 30);
        // do not recalculate colors if draw call was initiated by dragging
        if (!this.dragged) {
            let csOpt = this.getOptionByName("colorScale") as ColorScaleSelectorOption;
            let scale = chroma.scale(csOpt.input);
            let invOpt = this.getOptionByName("inverted") as GenericCheckBoxOption;
            this.previewColors = scale.colors(nSteps);
            if (invOpt.input) {
                this.previewColors = this.previewColors.reverse();
            }
        }
        let xStep = (this.width - 30) / nSteps;
        for (let i = 0; i < nSteps; i++) {
            this.ctx.fillStyle = this.previewColors[i];
            this.ctx.fillRect(this.xPos + 15 + i * xStep, this.yPos + this.height + 5, xStep, 20);
        }
    }

    protected _applyColors(): void {
        if (this.p !== undefined) {
            this._performNodeCalculation();
        }
    }

    protected _performNodeCalculation() {
        if (this.prevPlotData !== JSON.stringify(this.plotData)) {
            this.outputs[0].changed = true;
            this.prevPlotData = JSON.stringify(this.prevPlotData);
        }
        this.values = [this.plotData];
        super._performNodeCalculation();
    }
}

export class PointCloudNode extends _PlotNode {
    type: string = "pointCloud";
    /**
     * Point cloud plot node.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     * @param showPreviewCallback Function that is called when the export/publish button is pressed.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void, showPreviewCallback: (plotData: {}) => void) {
        super(name, x, y, canvas, updateCallback, showPreviewCallback);
        this.inputs.push(new ArrayConnector(this, 'x', true));
        this.inputs.push(new ArrayConnector(this, 'y', true));
        this.inputs.push(new ArrayConnector(this, 'z', true));
        this.options.push(
            new GenericNumberOption(this, "size", 1, "0.01", (function () { (this as PointCloudNode)._performNodeCalculation() }).bind(this))
        )
        this.calcHeight();
    }

    /**
     * 
     */
    protected _performNodeCalculation() {
        this.errors = [];
        if (
            this.getInputByName("x").isConnected &&
            this.getInputByName("y").isConnected &&
            this.getInputByName("z").isConnected &&
            (this.getInputByName("colorVar").isConnected || this.getInputByName("color").isConnected)
        ) {
            let coords = [];
            let x = this.getInputValueByName("x") as number[];
            let y = this.getInputValueByName("y") as number[];
            let z = this.getInputValueByName("z") as number[];
            if (x === undefined || y === undefined || z === undefined) {
                return
            }
            if (x.length !== y.length || x.length !== z.length) {
                this.errors.push("x, y, z have different lengths.");
                return
            }
            for (let i = 0; i < x.length; i++) {
                coords.push([x[i], y[i], z[i]]);
            }
            let colorVar: string[];
            if (this.colorBy === "direct") {
                const clr = this.getInputValueByName("color");
                if (clr === undefined) {
                    return;
                }
                if (Array.isArray(clr)) {
                    if (clr.length !== x.length) {
                        this.errors.push("Color array of incorrect length.")
                        return
                    }
                    colorVar = clr;
                } else {
                    colorVar = new Array(x.length);
                    colorVar.fill(clr);
                }
            } else {
                colorVar = this.getInputValueByName("colorVar");
                if (colorVar === undefined) {
                    return;
                }
                if (colorVar.length !== x.length) {
                    this.errors.push("ColorVar of incorrect length.")
                    return;
                }
            }
            this.plotData["plots"] = [{}]
            this.plotData["xScale"] = this.getOptionByName("xScale").getValue();
            this.plotData["yScale"] = this.getOptionByName("yScale").getValue();
            this.plotData["zScale"] = this.getOptionByName("zScale").getValue();
            this.plotData["plots"][0]["coordinates"] = coords;
            this.plotData["plots"][0]["plotType"] = "pointCloud";
            this.plotData["plots"][0]["colorBy"] = this.colorBy;
            this.plotData["plots"][0]["colorVar"] = colorVar
            this.plotData["plots"][0]["size"] = this.getOptionByName("size").getValue();
            this.plotData["plots"][0]["colorScale"] = this.getOptionByName("colorScale").getValue();
            this.plotData["plots"][0]["colorScaleInverted"] = this.getOptionByName("inverted").getValue();
            this.plotData["plots"][0]["showLegend"] = true;
            this.plotData["plots"][0]["fontSize"] = 11;
            this.plotData["plots"][0]["fontColor"] = "black";
            this.plotData["plots"][0]["legendTitle"] = null;
            this.plotData["plots"][0]["legendTitleFontSize"] = 16;
            this.plotData["plots"][0]["showAxes"] = [false, false, false];
            this.plotData["plots"][0]["axisLabels"] = ["X", "Y", "Z"];
            this.plotData["plots"][0]["axisColors"] = ["#666666", "#666666", "#666666"];
            this.plotData["plots"][0]["tickBreaks"] = [2, 2, 2];
            this.plotData["plots"][0]["showTickLines"] = [[false, false], [false, false], [false, false]];
            this.plotData["plots"][0]["tickLineColors"] = [["#aaaaaa", "#aaaaaa"], ["#aaaaaa", "#aaaaaa"], ["#aaaaaa", "#aaaaaa"]];
            this.plotData["plots"][0]["folded"] = false;
            this.plotData["plots"][0]["foldedEmbedding"] = null;
            this.plotData["plots"][0]["foldAnimDelay"] = null;
            this.plotData["plots"][0]["foldAnimDuration"] = null;
            this.plotData["plots"][0]["colnames"] = null;
            this.plotData["plots"][0]["rownames"] = null;
            let legendDataInput = this.getInputValueByName("legendData");
            if (legendDataInput !== undefined) {
                for (const key in legendDataInput) {
                    if (legendDataInput.hasOwnProperty(key)) {
                        this.plotData["plots"][0][key] = legendDataInput[key];
                    }
                }
            }
            let axisDataInput = this.getInputValueByName("axisData");
            if (axisDataInput !== undefined) {
                for (const key in axisDataInput) {
                    if (axisDataInput.hasOwnProperty(key)) {
                        this.plotData["plots"][0][key] = axisDataInput[key];
                    }
                }
            }
            super._performNodeCalculation();
        }
    }
}

export class ShapeCloudNode extends _PlotNode {
    type: string = "shapeCloud";
    /**
     * shapeCloud plot node.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     * @param showPreviewCallback Function that is called when the export/publish button is pressed.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void, showPreviewCallback: (plotData: {}) => void) {
        super(name, x, y, canvas, updateCallback, showPreviewCallback);
        this.inputs.push(new ArrayConnector(this, 'x', true));
        this.inputs.push(new ArrayConnector(this, 'y', true));
        this.inputs.push(new ArrayConnector(this, 'z', true));
        this.inputs.push(new ArrayConnector(this, 'dpInfo', true));
        this.options.push(
            new GenericTextOption(this, "name", "shapeCloud", (function () { (this as ShapeCloudNode)._performNodeCalculation() }).bind(this)),
            new GenericSelectorOption(this, "shape", "box", ["box", "sphere", "cylinder", "cone", "torus"], (function () { (this as ShapeCloudNode)._performNodeCalculation() }).bind(this)),
            new GenericCheckBoxOption(this, "legendShowShape", true, (function () { (this as ShapeCloudNode)._performNodeCalculation() }).bind(this)),
            new GenericNumberOption(this, "size", 1, "0.01", (function () { (this as ShapeCloudNode)._performNodeCalculation() }).bind(this))
        )
        this.calcHeight();
    }

    /**
     * 
     */
    protected _performNodeCalculation() {
        this.errors = [];
        if (
            this.getInputByName("x").isConnected &&
            this.getInputByName("y").isConnected &&
            this.getInputByName("z").isConnected &&
            (this.getInputByName("colorVar").isConnected || this.getInputByName("color").isConnected)
        ) {
            let coords = [];
            let x = this.getInputValueByName("x") as number[];
            let y = this.getInputValueByName("y") as number[];
            let z = this.getInputValueByName("z") as number[];
            if (x === undefined || y === undefined || z === undefined) {
                return
            }
            if (x.length !== y.length || x.length !== z.length) {
                this.errors.push("x, y, z have different lengths.");
                return;
            }
            let dpInfo: string[];
            if (this.getInputByName("dpInfo").isConnected) {
                dpInfo = this.getInputValueByName("dpInfo") as string[];
                if (dpInfo !== undefined && dpInfo.length !== x.length) {
                    this.errors.push("dpInfo has incorrect length.");
                    return;
                }
            }
            for (let i = 0; i < x.length; i++) {
                coords.push([x[i], y[i], z[i]]);
            }
            let colorVar: string[];
            if (this.colorBy === "direct") {
                const clr = this.getInputValueByName("color");
                if (Array.isArray(clr)) {
                    if (clr === undefined) {
                        return;
                    }
                    if (clr.length !== x.length) {
                        this.errors.push("Color array of incorrect length.")
                        return;
                    }
                    colorVar = clr;
                } else {
                    colorVar = new Array(x.length);
                    colorVar.fill(clr);
                }
            } else {
                colorVar = this.getInputValueByName("colorVar");
                if (colorVar === undefined) {
                    return;
                }
                if (colorVar.length !== x.length) {
                    this.errors.push("ColorVar of incorrect length.")
                    return;
                }
            }
            this.plotData["plots"] = [{}]
            this.plotData["xScale"] = this.getOptionByName("xScale").getValue();
            this.plotData["yScale"] = this.getOptionByName("yScale").getValue();
            this.plotData["zScale"] = this.getOptionByName("zScale").getValue();
            this.plotData["plots"][0]["coordinates"] = coords;
            this.plotData["plots"][0]["plotType"] = "shapeCloud";
            this.plotData["plots"][0]["colorBy"] = this.colorBy;
            this.plotData["plots"][0]["colorVar"] = colorVar;
            this.plotData["plots"][0]["dpInfo"] = dpInfo;
            this.plotData["plots"][0]["name"] = this.getOptionByName("name").getValue();
            this.plotData["plots"][0]["size"] = this.getOptionByName("size").getValue();
            this.plotData["plots"][0]["shape"] = this.getOptionByName("shape").getValue();
            this.plotData["plots"][0]["colorScale"] = this.getOptionByName("colorScale").getValue();
            this.plotData["plots"][0]["colorScaleInverted"] = this.getOptionByName("inverted").getValue();
            this.plotData["plots"][0]["showLegend"] = true;
            this.plotData["plots"][0]["legendShowShape"] = this.getOptionByName("legendShowShape").getValue();
            this.plotData["plots"][0]["fontSize"] = 11;
            this.plotData["plots"][0]["fontColor"] = "black";
            this.plotData["plots"][0]["legendTitle"] = null;
            this.plotData["plots"][0]["legendTitleFontSize"] = 16;
            this.plotData["plots"][0]["showAxes"] = [false, false, false];
            this.plotData["plots"][0]["axisLabels"] = ["X", "Y", "Z"];
            this.plotData["plots"][0]["axisColors"] = ["#666666", "#666666", "#666666"];
            this.plotData["plots"][0]["tickBreaks"] = [2, 2, 2];
            this.plotData["plots"][0]["showTickLines"] = [[false, false], [false, false], [false, false]];
            this.plotData["plots"][0]["tickLineColors"] = [["#aaaaaa", "#aaaaaa"], ["#aaaaaa", "#aaaaaa"], ["#aaaaaa", "#aaaaaa"]];
            let legendDataInput = this.getInputValueByName("legendData");
            if (legendDataInput !== undefined) {
                for (const key in legendDataInput) {
                    if (legendDataInput.hasOwnProperty(key)) {
                        this.plotData["plots"][0][key] = legendDataInput[key];
                    }
                }
            }
            let axisDataInput = this.getInputValueByName("axisData");
            if (axisDataInput !== undefined) {
                for (const key in axisDataInput) {
                    if (axisDataInput.hasOwnProperty(key)) {
                        this.plotData["plots"][0][key] = axisDataInput[key];
                    }
                }
            }
            super._performNodeCalculation();
        }
    }
}

export class SurfaceNode extends _PlotNode {
    type: string = "surface";
    /**
     * Surface plot node.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     * @param showPreviewCallback Function that is called when the export/publish button is pressed.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void, showPreviewCallback: (plotData: {}) => void) {
        super(name, x, y, canvas, updateCallback, showPreviewCallback);
        this.inputs.push(new Matrix2dConnector(this, 'height', true));
        this.calcHeight();
    }

    /**
     * 
     */
    protected _performNodeCalculation() {
        this.errors = [];
        if (
            this.getInputByName("height").isConnected &&
            (this.getInputByName("colorVar").isConnected || this.getInputByName("color").isConnected)
        ) {
            this.plotData["plots"] = [{}]
            this.plotData["xScale"] = this.getOptionByName("xScale").getValue();
            this.plotData["yScale"] = this.getOptionByName("yScale").getValue();
            this.plotData["zScale"] = this.getOptionByName("zScale").getValue();
            let coords = this.getInputValueByName("height") as number[][];
            let coordsFlat = Array.prototype.concat.apply([], coords) as number[];
            this.plotData["plots"][0]["coordinates"] = coords;
            this.plotData["plots"][0]["plotType"] = "surface";
            this.plotData["plots"][0]["colorBy"] = this.colorBy;
            if (this.colorBy === "direct") {
                const clr = this.getInputValueByName("color");
                if (clr === undefined) {
                    return;
                }
                let colorVar: string[];
                if (Array.isArray(clr)) {
                    colorVar = clr;
                } else {
                    colorVar = new Array(coordsFlat.length);
                    colorVar.fill(clr);
                }
                this.plotData["plots"][0]["colorVar"] = colorVar;
            } else {
                let colorVar = this.getInputValueByName("colorVar") as number[] | string[];
                if (colorVar === undefined) {
                    return;
                }
                if (colorVar.length !== coordsFlat.length) {
                    this.errors.push("colorVar has incorrect length.");
                    return;
                }
                this.plotData["plots"][0]["colorVar"] = colorVar;
            }
            this.plotData["plots"][0]["colorScale"] = this.getOptionByName("colorScale").getValue();
            this.plotData["plots"][0]["colorScaleInverted"] = this.getOptionByName("inverted").getValue();
            this.plotData["plots"][0]["showLegend"] = true;
            this.plotData["plots"][0]["fontSize"] = 11;
            this.plotData["plots"][0]["fontColor"] = "black";
            this.plotData["plots"][0]["legendTitle"] = null;
            this.plotData["plots"][0]["legendTitleFontSize"] = 16;
            this.plotData["plots"][0]["showAxes"] = [false, false, false];
            this.plotData["plots"][0]["axisLabels"] = ["X", "Y", "Z"];
            this.plotData["plots"][0]["axisColors"] = ["#666666", "#666666", "#666666"];
            this.plotData["plots"][0]["tickBreaks"] = [2, 2, 2];
            this.plotData["plots"][0]["showTickLines"] = [[false, false], [false, false], [false, false]];
            this.plotData["plots"][0]["tickLineColors"] = [["#aaaaaa", "#aaaaaa"], ["#aaaaaa", "#aaaaaa"], ["#aaaaaa", "#aaaaaa"]];
            this.plotData["plots"][0]["folded"] = false;
            this.plotData["plots"][0]["foldedEmbedding"] = null;
            this.plotData["plots"][0]["foldAnimDelay"] = null;
            this.plotData["plots"][0]["foldAnimDuration"] = null;
            this.plotData["plots"][0]["colnames"] = null;
            this.plotData["plots"][0]["rownames"] = null;
            let legendDataInput = this.getInputValueByName("legendData");
            if (legendDataInput !== undefined) {
                for (const key in legendDataInput) {
                    if (legendDataInput.hasOwnProperty(key)) {
                        this.plotData[key] = legendDataInput[key];
                    }
                }
            }
            let axisDataInput = this.getInputValueByName("axisData");
            if (axisDataInput !== undefined) {
                for (const key in axisDataInput) {
                    if (axisDataInput.hasOwnProperty(key)) {
                        this.plotData[key] = axisDataInput[key];
                    }
                }
            }
            super._performNodeCalculation();
        }
    }
}

export class HeatMapNode extends _PlotNode {
    type: string = "heatMap";
    /**
     * Heat map plot node.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     * @param showPreviewCallback Function that is called when the export/publish button is pressed.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void, showPreviewCallback: (plotData: {}) => void) {
        super(name, x, y, canvas, updateCallback, showPreviewCallback);
        this.inputs.push(new Matrix2dConnector(this, 'height', true));
        this.inputs.push(new ArrayConnector(this, 'colnames', true));
        this.inputs.push(new ArrayConnector(this, 'rownames', true));
        this.options.push(
            new GenericNumberOption(this, "size", 1, "0.01", (function () { (this as HeatMapNode)._performNodeCalculation() }).bind(this))
        )
        this.calcHeight();
    }

    /**
     * 
     */
    protected _performNodeCalculation() {
        this.errors = [];
        if (
            this.getInputByName("height").isConnected &&
            (this.getInputByName("colorVar").isConnected || this.getInputByName("color").isConnected)
        ) {
            this.plotData["plots"] = [{}]
            this.plotData["xScale"] = this.getOptionByName("xScale").getValue();
            this.plotData["yScale"] = this.getOptionByName("yScale").getValue();
            this.plotData["zScale"] = this.getOptionByName("zScale").getValue();
            let heightData = this.getInputValueByName("height");
            let dataFlat = Array.prototype.concat.apply([], heightData) as number[];
            this.plotData["plots"][0]["coordinates"] = heightData;
            this.plotData["plots"][0]["plotType"] = "heatMap";
            this.plotData["plots"][0]["colorBy"] = this.colorBy;
            if (this.colorBy === "direct") {
                const clr = this.getInputValueByName("color");
                if (clr === undefined) {
                    return;
                }
                let colorVar: string[];
                if (Array.isArray(clr)) {
                    colorVar = clr;
                } else {
                    colorVar = new Array(dataFlat.length);
                    colorVar.fill(clr);
                }
                this.plotData["plots"][0]["colorVar"] = colorVar;
            } else {
                let colorVar = this.getInputValueByName("colorVar") as number[] | string[];
                if (colorVar === undefined) {
                    return;
                }
                if (colorVar.length !== dataFlat.length) {
                    this.errors.push("colorVar has incorrect length.");
                    return;
                }
                this.plotData["plots"][0]["colorVar"] = colorVar;
            }
            this.plotData["plots"][0]["size"] = this.getOptionByName("size").getValue();
            this.plotData["plots"][0]["colorScale"] = this.getOptionByName("colorScale").getValue();
            this.plotData["plots"][0]["colorScaleInverted"] = this.getOptionByName("inverted").getValue();
            this.plotData["plots"][0]["showLegend"] = true;
            this.plotData["plots"][0]["fontSize"] = 11;
            this.plotData["plots"][0]["fontColor"] = "black";
            this.plotData["plots"][0]["legendTitle"] = null;
            this.plotData["plots"][0]["legendTitleFontSize"] = 16;
            let colnames = this.getInputValueByName("colnames");
            let rownames = this.getInputValueByName("rownames");
            if (colnames !== undefined && rownames !== undefined) {
                if (colnames.length !== heightData.length) {
                    this.errors.push("colnames has wrong number of items.");
                    return
                }
                if (rownames.length !== heightData[0].length) {
                    this.errors.push("rownames has wrong number of items.");
                    return
                }
                this.plotData["plots"][0]["colnames"] = colnames;
                this.plotData["plots"][0]["rownames"] = rownames;
                this.plotData["plots"][0]["showAxes"] = [true, true, true];
            } else {
                this.plotData["plots"][0]["showAxes"] = [false, false, false];
            }
            this.plotData["plots"][0]["axisLabels"] = ["X", "Y", "Z"];
            this.plotData["plots"][0]["axisColors"] = ["#666666", "#666666", "#666666"];
            this.plotData["plots"][0]["tickBreaks"] = [2, 2, 2];
            this.plotData["plots"][0]["showTickLines"] = [[false, false], [false, false], [false, false]];
            this.plotData["plots"][0]["tickLineColors"] = [["#aaaaaa", "#aaaaaa"], ["#aaaaaa", "#aaaaaa"], ["#aaaaaa", "#aaaaaa"]];
            this.plotData["plots"][0]["folded"] = false;
            this.plotData["plots"][0]["foldedEmbedding"] = null;
            this.plotData["plots"][0]["foldAnimDelay"] = null;
            this.plotData["plots"][0]["foldAnimDuration"] = null;
            let legendDataInput = this.getInputValueByName("legendData");
            if (legendDataInput !== undefined) {
                for (const key in legendDataInput) {
                    if (legendDataInput.hasOwnProperty(key)) {
                        this.plotData[key] = legendDataInput[key];
                    }
                }
            }
            let axisDataInput = this.getInputValueByName("axisData");
            if (axisDataInput !== undefined) {
                for (const key in axisDataInput) {
                    if (axisDataInput.hasOwnProperty(key)) {
                        this.plotData[key] = axisDataInput[key];
                    }
                }
            }
            super._performNodeCalculation();
        }
    }
}

export class ColorMapNode extends _Node {
    previewColors: string[];
    type: string = "heatMap";
    /**
     * Color mapping node.
     * 
     * This node can be used to map an array of numbers/categories to an array of color strings.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
        super(name, x, y, nodeColors.color, canvas, updateCallback);
        this.options.push(
            new GenericSelectorOption(this, "colorBy", "categories", ["categories", "values"], (function () { (this as ColorMapNode)._performNodeCalculation() }).bind(this)),
            new ColorScaleSelectorOption(this, "colorScale", "Paired", (function () { (this as ColorMapNode)._performNodeCalculation() }).bind(this)),
            new GenericCheckBoxOption(this, "inverted", false, (function () { (this as ColorMapNode)._performNodeCalculation() }).bind(this))
        );
        this.inputs.push(new ArrayConnector(this, "colorVar", true));
        this.outputs.push(new ColorConnector(this, "color", false));
        this.extraHeight = 35;
        this.calcHeight();
    }

    /**
     * Draw node and color scale preview.
     */
    public draw() {
        super.draw();
        this._drawPreview();
    }

    /**
     * Draws preview of selected color scale.
     */
    private _drawPreview() {
        let nSteps = 10;
        this.ctx.fillStyle = "white";
        this.ctx.fillRect(this.xPos + 10, this.yPos + this.height, this.width - 20, 30);
        // do not recalculate colors if draw call was initiated by dragging
        if (!this.dragged) {
            let csOpt = this.getOptionByName("colorScale") as ColorScaleSelectorOption;
            let scale = chroma.scale(csOpt.input);
            let invOpt = this.getOptionByName("inverted") as GenericCheckBoxOption;
            this.previewColors = scale.colors(nSteps);
            if (invOpt.input) {
                this.previewColors = this.previewColors.reverse();
            }
        }
        let xStep = (this.width - 30) / nSteps;
        for (let i = 0; i < nSteps; i++) {
            this.ctx.fillStyle = this.previewColors[i];
            this.ctx.fillRect(this.xPos + 15 + i * xStep, this.yPos + this.height + 5, xStep, 20);
        }
    }

    /**
     * 
     */
    protected _performNodeCalculation() {
        if (this.getInputByName("colorVar").isConnected) {
            let colorVar = this.inputValues[0] as any[];
            let colorBy = this.getOptionByName("colorBy").getValue();
            let colorScale = this.getOptionByName("colorScale").getValue() as string;
            let inverted = this.getOptionByName("inverted").getValue();
            let color = [];
            if (colorBy === "categories") {
                let groups = colorVar;
                let uniqueGroups = getUniqueVals(groups);
                let nColors = uniqueGroups.length;
                let colors: string[];
                if (inverted) {
                    colors = chroma.scale(chroma.brewer[colorScale]).domain([1, 0]).mode('lch').colors(nColors);
                } else {
                    colors = chroma.scale(chroma.brewer[colorScale]).mode('lch').colors(nColors);
                }
                for (let i = 0; i < nColors; i++) {
                    colors[i] += "ff";
                }
                for (let i = 0; i < colorVar.length; i++) {
                    let colorIndex = uniqueGroups.indexOf(groups[i]);
                    color.push(colors[colorIndex]);
                }
            } else {
                let min = colorVar.min();
                let max = colorVar.max();
                let colorfunc: chroma.Scale<chroma.Color>;
                if (inverted) {
                    colorfunc = chroma.scale(chroma.brewer[colorScale]).domain([1, 0]).mode('lch');
                } else {
                    colorfunc = chroma.scale(chroma.brewer[colorScale]).mode('lch');
                }
                // normalize the values to 0-1 range
                let norm = (colorVar as number[]).slice().map(v => (v - min) / (max - min));
                color = norm.map(v => colorfunc(v).alpha(1).hex("rgba"));
            }

            if (JSON.stringify(color) !== JSON.stringify(this.values[0])) {
                this.values = [color];
                this.outputs[0].changed = true;
            } else {
                this.outputs[0].changed = false;
            }
            super._performNodeCalculation();
        }
    }
}

export abstract class _DataNode extends _Node {
    data: any[][];
    width = 300;
    headers: string[] = [];
    rownames: string[] = [];
    datatype: string;

    /**
     * Abstract class for all node types that contain a data upload.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param color Color of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, color: string, canvas: HTMLCanvasElement, datatype: string, updateCallback: () => void) {
        super(name, x, y, color, canvas, updateCallback);
        this.datatype = datatype;
        this.options.push(new DataUploadOption(this, "upload data:"));
        this.calcHeight();
    }

    /**
     * Read selected file and parse into array of arrays.
     * @param evt File selection event.
     */
    public handleFiles(evt: Event, header: boolean = false, rownames: boolean = false) {
        let files = (<HTMLInputElement>evt.target).files;
        // $("#fileLabel").text(files[0].name);
        this.name = this.datatype + " - " + files[0].name;
        var reader = new FileReader();
        reader.onload = (function (this: _DataNode, event) {
            this.errors = [];
            var d = event.target.result as string;
            d = d.replace(/(\r\n|\n|\r)/gm, "\n");
            d = d.replace(/\n$/, "");
            var rows = d.split("\n");
            var rowItems = []
            for (let i = 0; i < rows.length; i++) {
                if (header && i === 0) {
                    this.headers = rows[i].split('\t');
                    continue;
                }
                rowItems.push(rows[i].split('\t'));
            }
            if (rowItems[0].length !== rowItems[1].length) {
                this.errors.push("1st and 2nd rows not of same lenght.");
            } else {
                var cols = [];
                for (let i = 0; i < rowItems.length; i++) {
                    const itemArray = rowItems[i];
                    for (let j = 0; j < itemArray.length; j++) {
                        const item = itemArray[j];
                        if (rownames) {
                            if (j === 0) {
                                this.rownames.push(item);
                                continue;
                            }
                            if (i === 0) {
                                cols.push([item]);
                            } else {
                                cols[j - 1].push(item);
                            }
                        } else {
                            if (i === 0) {
                                cols.push([item]);
                            } else {
                                cols[j].push(item);
                            }
                        }
                    }
                }

                this.data = cols;
                this._createConnectors();
            }
            this.calcHeight();
            this._showErrors();
            this.updateCallback();
        }).bind(this);
        reader.readAsText(files[0]);
    }

    /**
     * Create connectors based on the uploaded file.
     */
    protected _createConnectors() {
        // empty output array of previous connectors.
        this.outputs = [];
        // handled differently for each data type class.
    }
}

export class DataTableNode extends _DataNode {
    data: any[][];
    cols: string[];
    type: string = "dataTable";
    /**
     * Data table node.
     * 
     * Input should be a tab separated text file in a table format.
     * Array connectors will be created for each column, storing the rows in an array.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
        super(name, x, y, nodeColors.dataArray, canvas, "dataTable", updateCallback);
        this.options.unshift(new GenericCheckBoxOption(this, "includes header", true));
        this.calcHeight();
    }

    /**
     * Read selected file and parse into array of arrays.
     * @param evt File selection event.
     */
    public handleFiles(evt: Event) {
        super.handleFiles(evt, (this.options[0] as GenericCheckBoxOption).getValue() as boolean)
    }

    /**
     * Create connectors based on the uploaded file.
     */
    protected _createConnectors() {
        super._createConnectors();
        this.values = this.data;
        let nCols = this.data.length;
        for (let i = 0; i < nCols; i++) {
            if ((this.options[0] as GenericCheckBoxOption).getValue()) {
                this.outputs.push(new ArrayConnector(this, this.headers[i], false));
            } else {
                this.outputs.push(new ArrayConnector(this, "column " + i, false));
            }
        }
    }
}

export class Matrix2dNode extends _DataNode {
    type: string = "matrix2d";
    /**
     * Matrix 2d Node.
     * 
     * Input should be a tab separated text file in a table format.
     * All columns and rows should be of the same data type.
     * One matrix2d connector will be created.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
        super(name, x, y, nodeColors.dataMatrix, canvas, "matrix2d", updateCallback);
        this.options.unshift(new GenericCheckBoxOption(this, "includes row names", false));
        this.options.unshift(new GenericCheckBoxOption(this, "includes column names", false));
        this.calcHeight();
    }

    /**
     * Read selected file and parse into array of arrays.
     * @param evt File selection event.
     */
    public handleFiles(evt: Event) {
        super.handleFiles(
            evt,
            (this.options[0] as GenericCheckBoxOption).getValue() as boolean,
            (this.options[1] as GenericCheckBoxOption).getValue() as boolean
        )
    }

    /**
     * Create connectors based on the uploaded file.
     */
    protected _createConnectors() {
        super._createConnectors();
        this.values = [this.data];
        this.name += " - " + this.data.length + "x" + this.data[0].length;
        this.outputs.push(new Matrix2dConnector(this, "matrix", false));
        if (this.options[0].getValue()) {
            if (this.headers.length !== this.data.length) {
                this.errors.push("colnames has wrong number of items.")
                return
            }
            this.values.push(this.headers);
            this.outputs.push(new ArrayConnector(this, "colnames", false));
        }
        if (this.options[1].getValue()) {
            if (this.rownames.length !== this.data[0].length) {
                this.errors.push("rownames has wrong number of items.")
                return
            }
            this.values.push(this.rownames);
            this.outputs.push(new ArrayConnector(this, "rownames", false));
        }
    }
}

export class FlattenNode extends _Node {
    type: string = "flatten";
    /**
     * Flatten a matrix2d (array of arrays) input to an array.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
        super(name, x, y, nodeColors.dataArray, canvas, updateCallback);
        this.inputs.push(new Matrix2dConnector(this, "matrix2d", true));
        this.outputs.push(new ArrayConnector(this, "array", false));
        this.calcHeight();
    }

    /**
     * 
     */
    protected _performNodeCalculation() {
        let m2d = this.inputValues[0] as any[][];
        let flat = Array.prototype.concat.apply([], m2d);
        if (JSON.stringify(flat) !== JSON.stringify(this.values[0])) {
            this.outputs[0].changed = true;
            this.values = [flat];
        } else {
            this.outputs[0].changed = false;
        }
        super._performNodeCalculation();
    }
}

export class ArrayMathNode extends _Node {
    numInputs: number = 2;
    type: string = "arrayMath";
    /**
     * Do element-wise calculations on one or two arrays.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
        super(name, x, y, nodeColors.dataArray, canvas, updateCallback);
        this.width = 220;
        this.options.push(
            new GenericSelectorOption(this, "operation", "add", [
                "add",
                "multiply",
                "subtract",
                "divide",
                "greater than",
                "greater or equal",
                "less than",
                "less or equal",
                "equal to",
                "not equal to",
                "absolute",
                "power",
                "logarithm",
                "square root",
                "exponential",
                "sine",
                "cosine",
                "tangent",
                "arcsine",
                "arccosine",
                "arctangent"

            ], (function () { (this as ArrayMathNode)._switchOperation() }).bind(this))
        );
        this.inputs.push(new ArrayConnector(this, "array1", true));
        this.inputs.push(new ArrayConnector(this, "array2", true));
        this._switchOperation();
        this.outputs.push(new ArrayConnector(this, "array", false));
        this.calcHeight();
    }

    private _switchOperation() {
        if ([
            "sine",
            "cosine",
            "tangent",
            "arcsine",
            "arccosine",
            "arctangent",
            "exponential",
            "absolute",
            "square root",
            "logarithm"
        ].indexOf(this.options[0].getValue() as string) !== -1) {
            this.numInputs = 1;
            this.inputs[0].enable();
            this.inputs[1].disable();
        } else {
            this.numInputs = 2;
            this.inputs[0].enable();
            this.inputs[1].enable();
        }
        this.calcHeight();
        this._performNodeCalculation();
        this.updateCallback();
    }

    /**
     * 
     */
    protected _performNodeCalculation() {
        if (!this.inputs[0].isConnected || this.inputValues[0] === undefined) {
            return
        }
        if (this.numInputs > 1 && (!this.inputs[1].isConnected || this.inputValues[1] === undefined)) {
            return
        }
        let array1 = this.inputValues[0];
        let array2;
        if (this.numInputs > 1) {
            array2 = this.inputValues[1];
            if (array1.length !== array2.length) {
                this.errors.push("Input arrays are not of same length.");
                return
            }
        }
        let result = [];
        switch (this.getOptionByName("operation").getValue()) {
            case "add":
                for (let i = 0; i < array1.length; i++) {
                    result.push(parseFloat(array1[i]) + parseFloat(array2[i]));
                }
                break;
            case "multiply":
                for (let i = 0; i < array1.length; i++) {
                    result.push(parseFloat(array1[i]) * parseFloat(array2[i]));
                }
                break;
            case "subtract":
                for (let i = 0; i < array1.length; i++) {
                    result.push(parseFloat(array1[i]) - parseFloat(array2[i]));
                }
                break;
            case "divide":
                for (let i = 0; i < array1.length; i++) {
                    let divisor = parseFloat(array2[i])
                    if (divisor === 0) {
                        this.errors.push("Division by zero.");
                        return
                    }
                    result.push(parseFloat(array1[i]) / parseFloat(array2[i]));
                }
                break;
            case "greater than":
                for (let i = 0; i < array1.length; i++) {
                    result.push(parseFloat(array1[i]) > parseFloat(array2[i]) ? 1 : 0);
                }
                break;
            case "greater or equal":
                for (let i = 0; i < array1.length; i++) {
                    result.push(parseFloat(array1[i]) >= parseFloat(array2[i]) ? 1 : 0);
                }
                break;
            case "less than":
                for (let i = 0; i < array1.length; i++) {
                    result.push(parseFloat(array1[i]) < parseFloat(array2[i]) ? 1 : 0);
                }
                break;
            case "less or equal":
                for (let i = 0; i < array1.length; i++) {
                    result.push(parseFloat(array1[i]) <= parseFloat(array2[i]) ? 1 : 0);
                }
                break;
            case "equal to":
                for (let i = 0; i < array1.length; i++) {
                    result.push(parseFloat(array1[i]) === parseFloat(array2[i]) ? 1 : 0);
                }
                break;
            case "not equal to":
                for (let i = 0; i < array1.length; i++) {
                    result.push(parseFloat(array1[i]) !== parseFloat(array2[i]) ? 1 : 0);
                }
                break;
            case "absolute":
                for (let i = 0; i < array1.length; i++) {
                    result.push(Math.abs(parseFloat(array1[i])));
                }
                break;
            case "power":
                for (let i = 0; i < array1.length; i++) {
                    result.push(Math.pow(parseFloat(array1[i]), parseFloat(array2[i])));
                }
                break;
            case "logarithm":
                for (let i = 0; i < array1.length; i++) {
                    result.push(Math.log(parseFloat(array1[i])));
                }
                break;
            case "square root":
                for (let i = 0; i < array1.length; i++) {
                    result.push(Math.sqrt(parseFloat(array1[i])));
                }
                break;
            case "exponential":
                for (let i = 0; i < array1.length; i++) {
                    result.push(Math.exp(parseFloat(array1[i])));
                }
                break;
            case "sine":
                for (let i = 0; i < array1.length; i++) {
                    result.push(Math.sin(parseFloat(array1[i])));
                }
                break;
            case "cosine":
                for (let i = 0; i < array1.length; i++) {
                    result.push(Math.cos(parseFloat(array1[i])));
                }
                break;
            case "tangent":
                for (let i = 0; i < array1.length; i++) {
                    result.push(Math.tan(parseFloat(array1[i])));
                }
                break;
            case "arcsine":
                for (let i = 0; i < array1.length; i++) {
                    result.push(Math.asin(parseFloat(array1[i])));
                }
                break;
            case "arccosine":
                for (let i = 0; i < array1.length; i++) {
                    result.push(Math.acos(parseFloat(array1[i])));
                }
                break;
            case "arctangent":
                for (let i = 0; i < array1.length; i++) {
                    result.push(Math.atan(parseFloat(array1[i])));
                }
                break;
            default:
                break;
        }
        if (JSON.stringify(result) !== JSON.stringify(this.values[0])) {
            this.outputs[0].changed = true;
            this.values = [result.slice()];
            this.errors = [];
        } else {
            this.outputs[0].changed = false;
        }
        super._performNodeCalculation();
    }
}

export class MathNode extends _Node {
    numInputs: number = 2;
    type: string = "math";
    /**
     * Do calculations on one or two values.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
        super(name, x, y, nodeColors.dataValue, canvas, updateCallback);
        this.width = 220;
        this.options.push(
            new GenericSelectorOption(this, "operation", "add", [
                "add",
                "multiply",
                "subtract",
                "divide",
                "greater than",
                "greater or equal",
                "less than",
                "less or equal",
                "equal to",
                "not equal to",
                "absolute",
                "power",
                "logarithm",
                "square root",
                "exponential",
                "sine",
                "cosine",
                "tangent",
                "arcsine",
                "arccosine",
                "arctangent"

            ], (function () { (this as MathNode)._switchOperation() }).bind(this))
        );
        this.inputs.push(new ValueConnector(this, "value1", true));
        this.inputs.push(new ValueConnector(this, "value2", true));
        this._switchOperation();
        this.outputs.push(new ValueConnector(this, "result", false));
        this.calcHeight();
    }

    private _switchOperation() {
        if ([
            "sine",
            "cosine",
            "tangent",
            "arcsine",
            "arccosine",
            "arctangent",
            "exponential",
            "absolute",
            "square root",
            "logarithm"
        ].indexOf(this.options[0].getValue() as string) !== -1) {
            this.numInputs = 1;
            this.inputs[0].enable();
            this.inputs[1].disable();
        } else {
            this.numInputs = 2;
            this.inputs[0].enable();
            this.inputs[1].enable();
        }
        this.calcHeight();
        this._performNodeCalculation();
        this.updateCallback();
    }

    /**
     * 
     */
    protected _performNodeCalculation() {
        if (!this.inputs[0].isConnected || this.inputValues[0] === undefined) {
            return
        }
        if (this.numInputs > 1 && (!this.inputs[1].isConnected || this.inputValues[1] === undefined)) {
            return
        }
        let value1: number = parseFloat(this.inputValues[0]);
        let value2: number;
        if (this.numInputs > 1) {
            value2 = parseFloat(this.inputValues[1]);
        }
        let result: number;
        switch (this.getOptionByName("operation").getValue()) {
            case "add":
                result = value1 + value2;
                break;
            case "multiply":
                    result = value1 * value2;
                break;
            case "subtract":
                    result = value1 - value2;
                break;
            case "divide":
                    if (value2 === 0) {
                        this.errors.push("Division by zero.");
                        return
                    }
                    result = value1 / value2;
                break;
            case "greater than":
                    result = value1 > value2 ? 1 : 0;
                break;
            case "greater or equal":
                    result = value1 >= value2 ? 1 : 0;
                break;
            case "less than":
                    result = value1 < value2 ? 1 : 0;
                break;
            case "less or equal":
                    result = value1 <= value2 ? 1 : 0;
                break;
            case "equal to":
                    result = value1 === value2 ? 1 : 0;
                break;
            case "not equal to":
                    result = value1 !== value2 ? 1 : 0;
                break;
            case "absolute":
                    result = Math.abs(value1);
                break;
            case "power":
                    result = Math.pow(value1, value2);
                break;
            case "logarithm":
                    result = Math.log(value1);
                break;
            case "square root":
                    result = Math.sqrt(value1);
                break;
            case "exponential":
                    result = Math.exp(value1);
                break;
            case "sine":
                    result = Math.sin(value1);
                break;
            case "cosine":
                    result = Math.cos(value1);
                break;
            case "tangent":
                    result = Math.tan(value1);
                break;
            case "arcsine":
                    result = Math.asin(value1);
                break;
            case "arccosine":
                    result = Math.acos(value1);
                break;
            case "arctangent":
                    result = Math.atan(value1);
                break;
            default:
                break;
        }
        if (JSON.stringify(result) !== JSON.stringify(this.values[0])) {
            this.outputs[0].changed = true;
            this.values = [result];
            this.errors = [];
        } else {
            this.outputs[0].changed = false;
        }
        super._performNodeCalculation();
    }
}

// export class TiffNode extends _Node {
//     data: any[];
//     width = 300;

//     /**
//      * TODO
//      * 
//      * Upload node for tiff image stack files.
//      * 
//      * Output can directly be connected to an image stack node.
//      * @param id uuid identifying the node.
//      * @param name Name displayed as title of the node.
//      * @param x X coordinate of node. Defined as top left corner of node.
//      * @param y Y corrdinate of node. Defined as top left corner of node.
//      * @param canvas Canvas element of the node creator.
//      * @param updateCallback Function that is called when node has changed and needs redraw.
//      */
//     constructor(id: number, name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
//         super(id, name, x, y, nodeColors.tiff, canvas, updateCallback);
//         this.options.push(new DataUploadOption(this, "data"));
//         this.calcHeight();
//     }

//     /**
//      * Read selected file and parse into array of arrays.
//      * @param evt File selection event.
//      */
//     public handleFiles(evt: Event) {
//         let files = (<HTMLInputElement>evt.target).files;
//         // $("#fileLabel").text(files[0].name);
//         var reader = new FileReader();
//         reader.onload = (function (this: _DataNode, event) {
//             let ifds = UTIF.decode(event.target.result);
//             UTIF.decodeImage(event.target.result as ArrayBuffer, ifds[0]);
//         }).bind(this);
//         reader.readAsBinaryString(files[0]);
//     }

//     /**
//      * Create connectors based on the uploaded file.
//      */
//     protected _createConnectors() {
//         this.outputs = [];
//     }
// }

export class LegendDataNode extends _Node {
    type: string = "legendData";
    /**
     * legend data node.
     * 
     * This node can be used to customize the plot legend display.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
        super(name, x, y, nodeColors.legendData, canvas, updateCallback);
        this.options.push(
            new GenericCheckBoxOption(this, "show legend", true, (function () { (this as LegendDataNode)._performNodeCalculation() }).bind(this)),
            new GenericSelectorOption(this, "position", "right", ["right", "left"], (function () { (this as LegendDataNode)._performNodeCalculation() }).bind(this)),
            new GenericNumberOption(this, "font size", 11, "0.25", (function () { (this as LegendDataNode)._performNodeCalculation() }).bind(this)),
            new GenericTextOption(this, "font color", "black", (function () { (this as LegendDataNode)._performNodeCalculation() }).bind(this)),
            new GenericTextOption(this, "title", "", (function () { (this as LegendDataNode)._performNodeCalculation() }).bind(this)),
            new GenericNumberOption(this, "title font size", 11, "0.25", (function () { (this as LegendDataNode)._performNodeCalculation() }).bind(this)),
        );
        this.outputs.push(new LegendDataConnector(this, "legendData", false));
        this.calcHeight();
    }

    /**
     * 
     */
    protected _performNodeCalculation() {
        this.values = [{
            showLegend: this.options[0].getValue(),
            legendPosition: this.options[1].getValue(),
            fontSize: this.options[2].getValue(),
            fontColor: this.options[3].getValue(),
            legendTitle: this.options[4].getValue(),
            legendTitleFontSize: this.options[5].getValue(),
        }];
        this.outputs[0].changed = true;
        super._performNodeCalculation();
    }
}

export class AxisDataNode extends _Node {
    type: string = "axisData";
    /**
     * axis data node.
     * 
     * This node can be used to customize the plot axis display.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
        super(name, x, y, nodeColors.axisData, canvas, updateCallback);
        this.options.push(
            new GenericCheckBoxOption(this, "show x axis", false, (function () { (this as AxisDataNode)._performNodeCalculation() }).bind(this)),
            new GenericCheckBoxOption(this, "show y axis", false, (function () { (this as AxisDataNode)._performNodeCalculation() }).bind(this)),
            new GenericCheckBoxOption(this, "show z axis", false, (function () { (this as AxisDataNode)._performNodeCalculation() }).bind(this)),
            new GenericTextOption(this, "x axis label", "X", (function () { (this as AxisDataNode)._performNodeCalculation() }).bind(this)),
            new GenericTextOption(this, "y axis label", "Y", (function () { (this as AxisDataNode)._performNodeCalculation() }).bind(this)),
            new GenericTextOption(this, "z axis label", "Z", (function () { (this as AxisDataNode)._performNodeCalculation() }).bind(this)),
            new GenericTextOption(this, "x axis color", "#666666", (function () { (this as AxisDataNode)._performNodeCalculation() }).bind(this)),
            new GenericTextOption(this, "y axis color", "#666666", (function () { (this as AxisDataNode)._performNodeCalculation() }).bind(this)),
            new GenericTextOption(this, "z axis color", "#666666", (function () { (this as AxisDataNode)._performNodeCalculation() }).bind(this)),
            new GenericNumberOption(this, "x tick breaks", 2, "0.01", (function () { (this as AxisDataNode)._performNodeCalculation() }).bind(this)),
            new GenericNumberOption(this, "y tick breaks", 2, "0.01", (function () { (this as AxisDataNode)._performNodeCalculation() }).bind(this)),
            new GenericNumberOption(this, "z tick breaks", 2, "0.01", (function () { (this as AxisDataNode)._performNodeCalculation() }).bind(this)),
            new GenericCheckBoxOption(this, "show xy tick lines", false, (function () { (this as AxisDataNode)._performNodeCalculation() }).bind(this)),
            new GenericCheckBoxOption(this, "show xz tick lines", false, (function () { (this as AxisDataNode)._performNodeCalculation() }).bind(this)),
            new GenericCheckBoxOption(this, "show yx tick lines", false, (function () { (this as AxisDataNode)._performNodeCalculation() }).bind(this)),
            new GenericCheckBoxOption(this, "show yz tick lines", false, (function () { (this as AxisDataNode)._performNodeCalculation() }).bind(this)),
            new GenericCheckBoxOption(this, "show zx tick lines", false, (function () { (this as AxisDataNode)._performNodeCalculation() }).bind(this)),
            new GenericCheckBoxOption(this, "show zy tick lines", false, (function () { (this as AxisDataNode)._performNodeCalculation() }).bind(this)),
            new GenericTextOption(this, "xy tick line color", "#aaaaaa", (function () { (this as AxisDataNode)._performNodeCalculation() }).bind(this)),
            new GenericTextOption(this, "xz tick line color", "#aaaaaa", (function () { (this as AxisDataNode)._performNodeCalculation() }).bind(this)),
            new GenericTextOption(this, "yx tick line color", "#aaaaaa", (function () { (this as AxisDataNode)._performNodeCalculation() }).bind(this)),
            new GenericTextOption(this, "yz tick line color", "#aaaaaa", (function () { (this as AxisDataNode)._performNodeCalculation() }).bind(this)),
            new GenericTextOption(this, "zx tick line color", "#aaaaaa", (function () { (this as AxisDataNode)._performNodeCalculation() }).bind(this)),
            new GenericTextOption(this, "zy tick line color", "#aaaaaa", (function () { (this as AxisDataNode)._performNodeCalculation() }).bind(this)),
        );
        this.outputs.push(new AxisDataConnector(this, "axisData", false));
        this.calcHeight();
    }

    /**
     * 
     */
    protected _performNodeCalculation() {
        this.values = [{
            showAxes: [this.options[0].getValue(), this.options[1].getValue(), this.options[2].getValue()],
            axisLabels: [this.options[3].getValue(), this.options[4].getValue(), this.options[5].getValue()],
            axisColors: [this.options[6].getValue(), this.options[7].getValue(), this.options[8].getValue()],
            tickBreaks: [this.options[9].getValue(), this.options[10].getValue(), this.options[11].getValue()],
            showTickLines: [
                [this.options[12].getValue(), this.options[13].getValue()],
                [this.options[14].getValue(), this.options[15].getValue()],
                [this.options[16].getValue(), this.options[17].getValue()]
            ],
            tickLineColors: [
                [this.options[18].getValue(), this.options[19].getValue()],
                [this.options[20].getValue(), this.options[21].getValue()],
                [this.options[22].getValue(), this.options[23].getValue()]
            ]
        }];
        this.outputs[0].changed = true;
        super._performNodeCalculation();
    }
}

export class ColorNode extends _Node {
    type: string = "color";
    /**
     * color node.
     * 
     * This node creates a single color output.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
        super(name, x, y, nodeColors.color, canvas, updateCallback);
        this.options.push(
            new GenericTextOption(this, "color", "#e95420", (function () { (this as ColorNode)._performNodeCalculation() }).bind(this)),
        );
        this.outputs.push(new ColorConnector(this, "color", false));
        this.calcHeight();
        this._performNodeCalculation();
    }

    /**
     * 
     */
    protected _performNodeCalculation() {
        this.errors = [];
        if (this.values[0] !== this.options[0].getValue()) {
            this.values = [this.options[0].getValue()];
            this.outputs[0].changed = true;
        }
        let clr = this.getOptionByName("color").getValue();
        if (!chroma.valid(clr)) {
            this.errors.push("Not a valid color.");
            return
        }
        super._performNodeCalculation();
    }
}

export class ArrayNode extends _Node {
    lengthBy: string;
    fillWith: string;
    type: string = "array";
    /**
     * array Node
     * 
     * This node creates an array filled with one value.
     * It can determine the length of the array using either an option, or an input array.
     * @param id uuid identifiying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of the node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
        super(name, x, y, nodeColors.dataArray, canvas, updateCallback);
        this.options.push(
            new GenericSelectorOption(this, "choose length from", "option", ["option", "input"], (function () { (this as ArrayNode)._switchLengthBy() }).bind(this)),
            new GenericSelectorOption(this, "fill with", "value", ["value", "sequence", "range"], (function () { (this as ArrayNode)._switchFillWith() }).bind(this)),
            new GenericNumberOption(this, "value", 0, "0.001", (function () { (this as ArrayNode)._performNodeCalculation() }).bind(this)),
            new GenericNumberOption(this, "start", 0, "0.001", (function () { (this as ArrayNode)._performNodeCalculation() }).bind(this)),
            new GenericNumberOption(this, "end", 1, "0.001", (function () { (this as ArrayNode)._performNodeCalculation() }).bind(this)),
            new GenericNumberOption(this, "step", 1, "0.001", (function () { (this as ArrayNode)._performNodeCalculation() }).bind(this)),
            new GenericNumberOption(this, "length", 1, "1", (function () { (this as ArrayNode)._switchLengthBy() }).bind(this)),
        );
        this.inputs.push(new ArrayConnector(this, "length", true));
        this.inputs[0].disable();
        this._switchFillWith();
        this.lengthBy = "option";
        this.outputs.push(new ArrayConnector(this, "array", false));
        this.values[0] = [];
        this._performNodeCalculation();
        this.calcHeight();
    }

    /**
     * Show/hide the respective options and connectors depending on which length-by type was chosen.
     */
    private _switchLengthBy(): void {
        let lengthByOption = this.getOptionByName("choose length from") as GenericSelectorOption;
        let prevLengthBy = this.lengthBy;
        this.lengthBy = lengthByOption.input as string;
        if (prevLengthBy !== this.lengthBy) {
            let lengthInput = this.getInputByName("length");
            let lengthOption = this.getOptionByName("length");
            if (this.lengthBy === "option") {
                lengthInput.disable();
                lengthOption.enable();
            } else {
                lengthInput.enable();
                lengthOption.disable();
            }
        }
        this.calcHeight();
        this._performNodeCalculation();
        this.updateCallback();
    }

    private _switchFillWith() {
        let fillWithOption = this.getOptionByName("fill with") as GenericSelectorOption;
        let prevFillWith = this.fillWith;
        this.fillWith = fillWithOption.input as string;
        if (prevFillWith !== this.fillWith) {
            let valueInput = this.getOptionByName("value");
            let startInput = this.getOptionByName("start");
            let stepInput = this.getOptionByName("step");
            let endInput = this.getOptionByName("end");
            if (this.fillWith === "value") {
                valueInput.enable();
                startInput.disable();
                stepInput.disable();
                endInput.disable();
            } else if (this.fillWith === "sequence") {
                valueInput.disable();
                startInput.enable();
                stepInput.enable();
                endInput.disable();
            } else if (this.fillWith === "range") {
                valueInput.disable();
                startInput.enable();
                stepInput.disable();
                endInput.enable();
            }
        }
        this.calcHeight();
        this._performNodeCalculation();
        this.updateCallback();
    }

    /**
     * 
     */
    protected _performNodeCalculation() {
        this.errors = [];
        if (this.lengthBy === "option" || (this.lengthBy === "input" && this.getInputByName("length").isConnected)) {
            let len = this.getOptionByName("length").getValue();
            if (this.lengthBy === "input") {
                let lenghtInputValue = this.getInputValueByName("length");
                if (lenghtInputValue === undefined) {
                    return
                }
                len = lenghtInputValue.length;
            }
            let outputArray = new Array(len);
            let start: number;
            let step: number;
            switch (this.fillWith) {
                case "value":
                    outputArray.fill(this.getOptionByName("value").getValue());
                    break;
                case "sequence":
                    start = this.getOptionByName("start").getValue() as number;
                    step = this.getOptionByName("step").getValue() as number;
                    for (let i = 0; i < outputArray.length; i++) {
                        outputArray[i] = start + i * step;
                    }
                    break;
                case "range":
                    start = this.getOptionByName("start").getValue() as number;
                    let end = this.getOptionByName("end").getValue() as number;
                    step = (end - start) / outputArray.length;
                    for (let i = 0; i < outputArray.length; i++) {
                        outputArray[i] = start + i * step;
                    }
                    break;
                default:
                    break;
            }
            if (JSON.stringify(this.values[0]) !== JSON.stringify(outputArray)) {
                this.values = [outputArray.slice()];
                this.outputs[0].changed = true;
            }
            super._performNodeCalculation();
        }
    }
}

export class ValueNode extends _Node {
    type: string = "value";
    /**
     * value node.
     * 
     * This node creates a single value.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
        super(name, x, y, nodeColors.dataValue, canvas, updateCallback);
        this.options.push(
            new GenericTextOption(this, "value", "", (function () { (this as ValueNode)._performNodeCalculation() }).bind(this)),
        );
        this.outputs.push(new ValueConnector(this, "value", false));
        this.calcHeight();
        this._performNodeCalculation();
    }

    /**
     * 
     */
    protected _performNodeCalculation() {
        this.errors = [];
        if (this.values[0] !== this.options[0].getValue()) {
            this.values = [this.options[0].getValue()];
            this.outputs[0].changed = true;
        }
        super._performNodeCalculation();
    }
}

export class ToColorNode extends _Node {
    type: string = "toColor";
    /**
     * Ensures all items in an array node are valid colors and outputs a color array.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
        super(name, x, y, nodeColors.color, canvas, updateCallback);
        this.inputs.push(new ArrayConnector(this, "array", true));
        this.outputs.push(new ColorConnector(this, "color", false));
        this.calcHeight();
    }

    protected _performNodeCalculation() {
        if (!this.getInputByName("array").isConnected) {
            return
        }
        let arrInput = this.getInputValueByName("array");
        if (arrInput === undefined) {
            return
        }
        this.errors = [];
        for (let i = 0; i < arrInput.length; i++) {
            const col = arrInput[i];
            if (!chroma.valid(col)) {
                this.errors.push(col + " is not a valid color.")
                return
            }
        }
        if (JSON.stringify(this.values[0]) !== JSON.stringify(arrInput)) {
            this.values = [arrInput.slice()];
            this.outputs[0].changed = true;
        }
        super._performNodeCalculation();
    }
}

export class ToMatrixNode extends _Node {
    nRows: number;
    nCols: number;
    type: string = "toMatrix";
    /**
     * Create a matrix (array of arrays) from an array.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
        super(name, x, y, nodeColors.dataMatrix, canvas, updateCallback);
        this.options.push(
            new GenericNumberOption(this, "n rows", 1, "1", (function () { (this as ToMatrixNode)._performNodeCalculation() }).bind(this)),
            new GenericNumberOption(this, "n columns", 1, "1", (function () { (this as ToMatrixNode)._performNodeCalculation() }).bind(this))
        )
        this.inputs.push(new ArrayConnector(this, "array", true));
        this.outputs.push(new Matrix2dConnector(this, "matrix2d", false));
        this.calcHeight();
    }

    /**
     * 
     */
    protected _performNodeCalculation() {
        this.errors = [];
        if (!this.inputs[0].isConnected || this.getInputValueByName("array") === undefined) {
            return
        }
        let inArray = this.getInputValueByName("array") as number[] | string[];
        let len = inArray.length;
        let rowOption = this.getOptionByName("n rows") as GenericNumberOption;
        let colOption = this.getOptionByName("n columns") as GenericNumberOption;
        let nRows = rowOption.input;
        let nCols = colOption.input;
        if (len / nRows === nCols) {
            this.nRows = nRows;
            this.nCols = nCols;
        } else {
            if (nRows === this.nRows && nCols !== this.nCols) {
                // nCols was changed by the user, try to recalculate a number of rows.
                let putNRows = len / nCols;
                if (!(Math.floor(putNRows) === putNRows)) {
                    this.errors.push("Cannot create a matrix.")
                    return
                }
                this.nRows = putNRows;
                this.nCols = nCols;
            } else if (nRows !== this.nRows && nCols === this.nCols) {
                // nRows was changed by the user, try to recalculate a number of columns.
                let putNCols = len / nRows;
                if (!(Math.floor(putNCols) === putNCols)) {
                    this.errors.push("Cannot create a matrix.")
                    return
                }
                this.nRows = nRows;
                this.nCols = putNCols;
            } else {
                // both changed or initial calculation
                this.nRows = 1;
                this.nCols = len;
            }
        }
        colOption.changeValue(this.nCols);
        rowOption.changeValue(this.nRows);
        let outputMatrix = [];
        for (let i = 0; i < this.nRows; i++) {
            outputMatrix.push([]);
            for (let j = 0; j < this.nCols; j++) {
                outputMatrix[i].push(inArray[i * this.nCols + j]);
            }
        }
        if (JSON.stringify(this.values[0]) !== JSON.stringify(outputMatrix)) {
            this.values = [outputMatrix];
            this.outputs[0].changed = true;
        }
        super._performNodeCalculation();
    }
}

export class ConcatNode extends _Node {
    n: number = 3;
    type: string = "concatenate";
    /**
     * Concatenate two or more arrays into one.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
        super(name, x, y, nodeColors.dataArray, canvas, updateCallback);
        this.options.push(
            new GenericButtonOption(this, "clear inputs", "fas fa-minus", (this as ConcatNode)._clear_inputs.bind(this))
        )
        this.inputs.push(new ArrayConnector(this, "array1", true));
        this.inputs.push(new ArrayConnector(this, "array2", true));
        this.outputs.push(new ArrayConnector(this, "array", false));
        this.calcHeight();
    }

    private _clear_inputs() {
        let firstFree = true;
        let toRemove = [];
        for (let i = 1; i < this.inputs.length; i++) {
            if (!this.inputs[i].isConnected) {
                if (firstFree) {
                    firstFree = false;
                } else {
                    toRemove.push(i);
                }
            }
        }
        for (let i = this.inputs.length - 1; i >= 0; i--) {
            if (toRemove.indexOf(i) !== -1) {
                this.inputs.splice(i, 1);
            }
        }
        this.calcHeight();
    }

    protected _performNodeCalculation() {
        let freeInputs = 0;
        let outArr = [];
        for (let i = 0; i < this.inputs.length; i++) {
            const inArrInput = this.inputs[i];
            if (inArrInput.isConnected) {
                let inArr = this.getInputValue(inArrInput);
                if (inArr === undefined) {
                    continue;
                }
                outArr = outArr.concat(inArr);
            } else {
                freeInputs += 1;
            }
        }
        if (freeInputs === 0) {
            this.inputs.push(new ArrayConnector(this, "array" + (this.n).toString(), true));
            this.calcHeight();
            this.n += 1;
        }
        if (JSON.stringify(this.values[0]) !== JSON.stringify(outArr)) {
            this.values = [outArr.slice()];
            this.outputs[0].changed = true;
        }
        super._performNodeCalculation();
    }
}

export class SplitNode extends _Node {
    private inArr = [];
    private prevN = 1;
    private prevSplit = "once at n";
    type: string = "split";
    /**
     * Splits an array based on rules given in options.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
        super(name, x, y, nodeColors.dataArray, canvas, updateCallback);
        this.inputs.push(new ArrayConnector(this, "array", true));
        this.options.push(
            new GenericSelectorOption(this, "split", "once at n", ["once at n", "every n"], (function () { (this as SplitNode)._performNodeCalculation() }).bind(this)),
            new GenericNumberOption(this, "n", 1, "1", (function () { (this as SplitNode)._performNodeCalculation() }).bind(this))
        )
        this.calcHeight();
    }

    protected _performNodeCalculation() {
        if (!this.inputs[0].isConnected) {
            return;
        }
        let inArr = this.getInputValue(this.inputs[0]) as any[];
        if (inArr === undefined) {
            return;
        }
        let n = this.getOptionByName("n").getValue() as number;
        let split = this.getOptionByName("split").getValue() as string;
        if (JSON.stringify(inArr) === JSON.stringify(this.inArr) && split === this.prevSplit && n === this.prevN) {
            return;
        }
        this.prevSplit = split;
        this.prevN = n;
        this.inArr = inArr;
        // remove previous outputs and dissolve connections
        for (let i = 0; i < this.outputs.length; i++) {
            const output = this.outputs[i];
            if (output.isConnected) {
                output.destroy();
            }
        }
        this.outputs = [];
        // create new outputs
        if (split === "once at n") {
            this.outputs.push(
                new ArrayConnector(this, "array 0:" + n.toString(), false),
                new ArrayConnector(this, "array " + n.toString() + ":" + inArr.length, false)
            )

            this.values = [inArr.slice(0, n), inArr.slice(n)];
            this.outputs[0].changed = true;
            this.outputs[1].changed = true;
        } else {
            this.outputs.push(new ArrayConnector(this, "array 0:" + n.toString(), false))
            this.outputs[0].changed = true;
            this.values = [inArr.slice(0, n)];
            let remainder = inArr.slice(n);
            while (remainder.length > 0) {
                let nArr = remainder.slice(0, n)
                this.values.push(nArr);
                remainder = remainder.slice(n);
                this.outputs.push(new ArrayConnector(this, "array " + (n * this.outputs.length).toString() + ":" + (n * this.outputs.length + nArr.length).toString(), false));
                this.outputs[this.outputs.length - 1].changed = true;
            }
        }

        this.calcHeight();
        super._performNodeCalculation();
    }
}

export class SubsetNode extends _Node {
    inArray: any[] = [];
    subsetArray: number[] = [];
    type: string = "subset";
    /**
     * Subsets an array by another array of 0s and 1s.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
        super(name, x, y, nodeColors.dataArray, canvas, updateCallback);
        this.inputs.push(new ArrayConnector(this, "array", true));
        this.inputs.push(new ArrayConnector(this, "subset by", true));
        this.outputs.push(new ArrayConnector(this, "array", false));
        this.calcHeight();
    }

    protected _performNodeCalculation() {
        this.errors = []; // clear errors
        // check if all input connectors are connected and have values
        if (!this.inputs[0].isConnected || !this.inputs[1].isConnected) {
            return;
        }
        let inArray = this.getInputValueByName("array") as any[];
        if (inArray === undefined) {
            return;
        }
        let subsetArray = this.getInputValueByName("subset by") as number[];
        if (subsetArray === undefined) {
            return;
        }
        // check if input array and subset array are of same length
        if (inArray.length !== subsetArray.length) {
            this.errors.push("arrays not of same length.")
        }
        // check if there has been a change since the last time the output value was calculated
        if (JSON.stringify(inArray) === JSON.stringify(this.inArray) && JSON.stringify(subsetArray) === JSON.stringify(this.subsetArray)) {
            return;
        }
        // subset the array
        let outArray = [];
        for (let i = 0; i < subsetArray.length; i++) {
            const sub = subsetArray[i];
            if (sub === 1) {
                outArray.push(inArray[i]);
            } else if (sub === 0) {
                continue;
            } else {
                this.errors.push('"subset by" has invalid values.');
                return;
            }
        }
        this.values = [outArray.slice()];
        this.outputs[0].changed = true;
        this.inArray = inArray;
        this.subsetArray = subsetArray;
        super._performNodeCalculation();
    }
}

export class FilterNode extends _Node {
    inArray: any[] = [];
    prevFilter: string = "";
    type: string = "filter";
    /**
     * Filters a string array by a value.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
        super(name, x, y, nodeColors.dataArray, canvas, updateCallback);
        this.options.push(
            new GenericTextOption(this, "filter", "", (function () { (this as FilterNode)._performNodeCalculation() }).bind(this))
        );
        this.inputs.push(new ArrayConnector(this, "array", true));
        this.outputs.push(new ArrayConnector(this, "array", false));
        this.calcHeight();
    }

    protected _performNodeCalculation() {
        this.errors = []; // clear errors
        // check if input connector is connected and has values
        if (!this.inputs[0].isConnected) {
            return;
        }
        let inArray = this.getInputValueByName("array") as any[];
        if (inArray === undefined) {
            return;
        }
        let filterStr = this.getOptionByName("filter").getValue() as string;
        
        // check if there has been a change since the last time the output value was calculated
        if (JSON.stringify(inArray) === JSON.stringify(this.inArray) && filterStr === this.prevFilter) {
            return;
        }
        // create filter array
        let outArray = inArray.map(function(val) {
            if (val === filterStr) {
                return 1
            } else {
                return 0
            }
        });
        this.values = [outArray.slice()];
        this.outputs[0].changed = true;
        this.inArray = inArray;
        this.prevFilter = filterStr;
        super._performNodeCalculation();
    }
}

export class GroupNode extends _Node {
    inArray: any[] = [];
    type: string = "group";
    /**
     * Creates arrays of 0s and 1s for each unique value in the input array.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
        super(name, x, y, nodeColors.dataArray, canvas, updateCallback);
        this.inputs.push(new ArrayConnector(this, "array", true));
        this.calcHeight();
    }

    protected _performNodeCalculation() {
        if (!this.inputs[0].isConnected) {
            return;
        }
        let inArray = this.getInputValue(this.inputs[0]) as any[];
        if (inArray === undefined) {
            return;
        }
        if (JSON.stringify(inArray) === JSON.stringify(this.inArray)) {
            return;
        }
        this.inArray = inArray;
        // remove previous outputs and dissolve connections
        for (let i = 0; i < this.outputs.length; i++) {
            const output = this.outputs[i];
            if (output.isConnected) {
                output.destroy();
            }
        }
        this.outputs = [];
        this.values = [];
        // create new outputs

        let uniqVals = [];
        for (let i = 0; i < inArray.length; i++) {
            const val = inArray[i];
            // 
            let posInUniq = uniqVals.indexOf(val)
            if (posInUniq === -1) {
                if (this.values.length === 0) {
                    this.values.push([1]);
                } else {
                    let tmpArr = new Array(this.values[0].length);
                    this.values.push(tmpArr.fill(0));
                    this.values[this.values.length - 1].push(1);
                }
                this.outputs.push(new ArrayConnector(this, val, false));
                this.outputs[this.outputs.length - 1].changed = true;
                uniqVals.push(val);
                for (let j = 0; j < this.values.length - 1; j++) {
                    this.values[j].push(0);
                }
            } else {
                for (let j = 0; j < this.values.length; j++) {
                    if (j === posInUniq) {
                        this.values[j].push(1);
                    } else {
                        this.values[j].push(0);
                    }
                }
            }
        }

        this.calcHeight();
        super._performNodeCalculation();
    }
}

export class GetColumnNode extends _Node {
    inMatrix: any[][];
    prevColumn: number;
    type: string = "getColumn";
    /**
     * Gets a column of a matrix.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
        super(name, x, y, nodeColors.dataArray, canvas, updateCallback);
        this.options.push(
            new GenericNumberOption(this, "column", 0, "1", (function () { (this as GetColumnNode)._performNodeCalculation() }).bind(this))
        );
        this.inputs.push(new Matrix2dConnector(this, "matrix", true));
        this.outputs.push(new ArrayConnector(this, "array", false));
        this.calcHeight();
    }

    protected _performNodeCalculation() {
        this.errors = []; // clear errors
        // check if input connector is connected and has values
        if (!this.inputs[0].isConnected) {
            return;
        }
        let inMatrix = this.getInputValueByName("matrix") as any[][];
        if (inMatrix === undefined) {
            return;
        }
        let columnN = this.getOptionByName("column").getValue() as number;
        
        // check if there has been a change since the last time the output value was calculated
        if (JSON.stringify(inMatrix) === JSON.stringify(this.inMatrix) && columnN === this.prevColumn) {
            return;
        }
        if (columnN < 0 || columnN > inMatrix[0].length) {
            this.errors.push("column index out of bounds.");
            return;
        }
        // get column
        let outArray = [];
        for (let i = 0; i < inMatrix.length; i++) {
            const row = inMatrix[i];
            outArray.push(row[columnN]);
        }
        this.values = [outArray.slice()];
        this.outputs[0].changed = true;
        this.inMatrix = inMatrix;
        this.prevColumn = columnN;
        super._performNodeCalculation();
    }
}

export class GetRowNode extends _Node {
    inMatrix: any[][];
    prevRow: number;
    type: string = "getRow";
    /**
     * Gets a row of a matrix.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
        super(name, x, y, nodeColors.dataArray, canvas, updateCallback);
        this.options.push(
            new GenericNumberOption(this, "row", 0, "1", (function () { (this as GetRowNode)._performNodeCalculation() }).bind(this))
        );
        this.inputs.push(new Matrix2dConnector(this, "matrix", true));
        this.outputs.push(new ArrayConnector(this, "array", false));
        this.calcHeight();
    }

    protected _performNodeCalculation() {
        this.errors = []; // clear errors
        // check if input connector is connected and has values
        if (!this.inputs[0].isConnected) {
            return;
        }
        let inMatrix = this.getInputValueByName("matrix") as any[][];
        if (inMatrix === undefined) {
            return;
        }
        let rowN = this.getOptionByName("row").getValue() as number;
        
        // check if there has been a change since the last time the output value was calculated
        if (JSON.stringify(inMatrix) === JSON.stringify(this.inMatrix) && rowN === this.prevRow) {
            return;
        }
        if (rowN < 0 || rowN > inMatrix.length) {
            this.errors.push("row index out of bounds.");
            return;
        }

        this.values = [inMatrix[rowN].slice()];
        this.outputs[0].changed = true;
        this.inMatrix = inMatrix;
        this.prevRow = rowN;
        super._performNodeCalculation();
    }
}

export class IndexNode extends _Node {
    inArray: any[];
    prevIndex: number;
    type: string = "index";
    /**
     * Gets the value of an array at the provided index.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
        super(name, x, y, nodeColors.dataValue, canvas, updateCallback);
        this.options.push(
            new GenericNumberOption(this, "index", 0, "1", (function () { (this as IndexNode)._performNodeCalculation() }).bind(this))
        );
        this.inputs.push(new ArrayConnector(this, "array", true));
        this.outputs.push(new ValueConnector(this, "value", false));
        this.calcHeight();
    }

    protected _performNodeCalculation() {
        this.errors = []; // clear errors
        // check if input connector is connected and has values
        if (!this.inputs[0].isConnected) {
            return;
        }
        let inArray = this.getInputValueByName("array") as any[];
        if (inArray === undefined) {
            return;
        }
        let n = this.getOptionByName("index").getValue() as number;
        
        // check if there has been a change since the last time the output value was calculated
        if (JSON.stringify(inArray) === JSON.stringify(this.inArray) && n === this.prevIndex) {
            return;
        }
        if (n < 0 || n > inArray.length) {
            this.errors.push("index out of bounds.");
            return;
        }

        this.values = [inArray[n]];
        this.outputs[0].changed = true;
        this.inArray = inArray;
        this.prevIndex = n;
        super._performNodeCalculation();
    }
}

export class FindNode extends _Node {
    inArray: any[];
    prevSearch: string;
    type: string = "find";
    /**
     * Gets the index of a search value in an array. First occurrecne if multiple.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
        super(name, x, y, nodeColors.dataValue, canvas, updateCallback);
        this.options.push(
            new GenericTextOption(this, "search", "", (function () { (this as FindNode)._performNodeCalculation() }).bind(this))
        );
        this.inputs.push(new ArrayConnector(this, "array", true));
        this.outputs.push(new ValueConnector(this, "value", false));
        this.calcHeight();
    }

    protected _performNodeCalculation() {
        this.errors = []; // clear errors
        // check if input connector is connected and has values
        if (!this.inputs[0].isConnected) {
            return;
        }
        let inArray = this.getInputValueByName("array") as any[];
        if (inArray === undefined) {
            return;
        }
        let searchValue = this.getOptionByName("search").getValue() as string;
        
        // check if there has been a change since the last time the output value was calculated
        if (JSON.stringify(inArray) === JSON.stringify(this.inArray) && searchValue === this.prevSearch) {
            return;
        }

        let index = inArray.indexOf(searchValue);
        if (index === -1) {
            this.errors.push("Value not found.");
            return;
        }

        this.values = [index];
        this.outputs[0].changed = true;
        this.inArray = inArray;
        this.prevSearch = searchValue;
        super._performNodeCalculation();
    }
}

export class MinMaxNode extends _Node {
    inArray: any[];
    prevOpt: string;
    type: string = "minMax";
    /**
     * Gets the minimum or maximum value of the input array.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
        super(name, x, y, nodeColors.dataValue, canvas, updateCallback);
        this.options.push(
            new GenericSelectorOption(this, "get", "minimum", ["minimum", "maximum"], (function () { (this as MinMaxNode)._performNodeCalculation() }).bind(this))
        );
        this.inputs.push(new ArrayConnector(this, "array", true));
        this.outputs.push(new ValueConnector(this, "value", false));
        this.calcHeight();
    }

    protected _performNodeCalculation() {
        this.errors = []; // clear errors
        // check if input connector is connected and has values
        if (!this.inputs[0].isConnected) {
            return;
        }
        let inArray = this.getInputValueByName("array") as any[];
        if (inArray === undefined) {
            return;
        }
        let opt = this.getOptionByName("get").getValue() as string;
        
        // check if there has been a change since the last time the output value was calculated
        if (JSON.stringify(inArray) === JSON.stringify(this.inArray) && opt === this.prevOpt) {
            return;
        }

        let result: number;
        if (opt === "minimum") {
            result = inArray.min();
        } else {
            result = inArray.max();
        }

        this.values = [result];
        this.outputs[0].changed = true;
        this.inArray = inArray;
        this.prevOpt = opt;
        super._performNodeCalculation();
    }
}

export class SelectorNode extends _Node {
    inArray: any[];
    prevChoice: string;
    type: string = "selector";
    /**
     * Gets the minimum or maximum value of the input array.
     * @param id uuid identifying the node.
     * @param name Name displayed as title of the node.
     * @param x X coordinate of node. Defined as top left corner of node.
     * @param y Y corrdinate of node. Defined as top left corner of node.
     * @param canvas Canvas element of the node creator.
     * @param updateCallback Function that is called when node has changed and needs redraw.
     */
    constructor(name: string, x: number, y: number, canvas: HTMLCanvasElement, updateCallback: () => void) {
        super(name, x, y, nodeColors.dataValue, canvas, updateCallback);
        this.options.push(
            new GenericSelectorOption(this, "select", "", [""], (function () { (this as SelectorNode)._performNodeCalculation() }).bind(this))
        );
        this.inputs.push(new ArrayConnector(this, "array", true));
        this.outputs.push(new ValueConnector(this, "value", false));
        this.calcHeight();
    }

    protected _performNodeCalculation() {
        this.errors = []; // clear errors
        // check if input connector is connected and has values
        if (!this.inputs[0].isConnected) {
            return;
        }
        let inArray = this.getInputValueByName("array") as any[];
        if (inArray === undefined) {
            return;
        }
        let opt = this.getOptionByName("select") as GenericSelectorOption;
        let optVal = opt.getValue() as string;

        let inArrChanged = JSON.stringify(inArray) !== JSON.stringify(this.inArray)
        
        // check if there has been a change since the last time the output value was calculated
        if (!inArrChanged && optVal === this.prevChoice) {
            return;
        }

        if (inArrChanged) {
            let choices = Array.from(new Set(inArray)); // get unique values
            optVal = choices[0];
            opt.changeValue(optVal, choices);
        }

        this.values = [optVal];
        this.outputs[0].changed = true;
        this.inArray = inArray;
        this.prevChoice = optVal;
        super._performNodeCalculation();
    }
}